import {
  type Filter,
  type Query,
  TimeDimensionGranularity,
} from "@cubejs-client/core"
import {
  AggregationType,
  UNKNOWN_DATA_KEY,
  TOTAL_CALCULATED_DATA_KEY,
} from "./charts"
import { MEDIA_CHANNEL_TO_SOURCE_ID_MAPPING } from "../components/features/dashboards/BrandMediaOptimizationDashboard/constants"

import useDate from "~/ui-rtk/hooks/date"
import { safeDivide } from "../utils/number-utils"
import {
  SHOPIFY_REVENUE_TYPE,
  SHOPIFY_REVENUE_TYPE_TO_COLOR_MAP,
  SHOPIFY_REVENUE_TYPE_TO_TITLE_MAP,
} from "./revenue"
import { DEFAULT_DEP_VAR_METRIC } from "../hooks/dep-var"
import type {
  CompanyDto,
  CompanyMetadataDto,
} from "~/ui-rtk/api/types/company.types"
import { resolveRowAssets } from "../utils/resolve-row-assets"
import { resolveTimeDimensionKey } from "../utils/cube"
import type { AppDispatch } from "~/ui-rtk/app/store"
import {
  REVENUE_KPI_MEASURES,
  filterFbReachMeasuresByGranularity,
} from "~/shared/kpi"

const dayjs = useDate()

// Type definitions for metric data structures
export type MetricValue = string | number | boolean | null | undefined

export type BrandData = {
  searches: number
  share_of_search?: number
}

export type BrandDataMap = Record<string, BrandData>

export type TooltipGroup = Record<
  string,
  { searches: number; share_of_search: number }
>

// MetricDataItem represents dynamic metric data from Cube.js API
// Using unknown because Cube.js returns highly dynamic data structures
export type MetricDataItem = Record<string, unknown>

// ProcessedMetricItem represents transformed metric data after post-processing
export type ProcessedMetricItem = Record<string, unknown>

export type ProcessedDataResult = ProcessedMetricItem[]

export type ColumnConfig = {
  dataIndex: string
  key: string
  shortTitle?: string
  title: string
  type: string
}

export type MetricsConfig = ColumnConfig[] | string[]

export type PostProcessResultSync =
  | [ProcessedDataResult]
  | [ProcessedDataResult, MetricsConfig]

export type PostProcessResult =
  | PostProcessResultSync
  | Promise<PostProcessResultSync>

export type FilterResult =
  | ProcessedDataResult
  | { metrics: ProcessedDataResult; metricsConfig: MetricsConfig }

export type PostProcessParams = Record<string, unknown>

export type FilterOptions = Record<string, unknown>

export type QueryParams = Record<string, unknown>

export type CompanyKeywords = NonNullable<CompanyMetadataDto["keywords"]>

export type GoogleKeywords = NonNullable<CompanyKeywords["google"]>

export type CompareFn<T = MetricDataItem> = (a: T, b: T) => number

export const GranularityMapping: Record<
  AggregationType,
  TimeDimensionGranularity
> = {
  [AggregationType.DAILY]: "day",
  [AggregationType.MONTHLY]: "month",
  [AggregationType.WEEKLY]: "week",
}

export type TMetric = Partial<Query>

export const TAG_ASSIGNMENTS_KEY = "__tagAssignments"

/**
 * When set, the data loader subscribes to bulk-get tag-assignments alongside
 * postProcess so the table refreshes after a save without waiting for the
 * cube query to re-run.
 */
export type EnrichTagsConfig = {
  itemType: "ad" | "adset" | "campaign"
  /** Row field that holds the platform external id (post prefix-strip). */
  idKey: string
}

/**
 * Configures fire-and-forget asset resolution for a chart's rows. The data
 * loader reads this after the cube cache is populated and warms the asset
 * cache (used by MarathonImage). Set on charts whose rows reference media
 * (ad creatives, organic posts, etc.).
 */
export type AssetFieldsConfig = {
  /** Row field with the platform/source name (e.g. "media_channel"). */
  sourceTypeKey: string
  /** Row field with the external ref id (e.g. "ad_id", "post_id"). */
  externalRefIdKey: string
  /** Optional row field with a fallback URL. */
  urlKey?: string
  /** When true, organic source types (INSTAGRAM/TIKTOK/YOUTUBE) are used instead of *_AD. */
  isOrganic?: boolean
}

export type TQuery = {
  q: TMetric | TMetric[] | ((params: QueryParams) => TMetric | TMetric[])
  postProcess?: (
    data: MetricDataItem[],
    params: PostProcessParams,
  ) => PostProcessResult
  getCubePrefix?: (params: QueryParams) => string
  disabled?: boolean
  filter?: (
    data: MetricDataItem[],
    granularity: TimeDimensionGranularity,
    options: FilterOptions,
  ) => FilterResult
  enrichTags?: EnrichTagsConfig
  assetFields?: AssetFieldsConfig
}
export type TMetricsMap = Record<string, TQuery>

export type TMetricItem = Record<string, string | number>
export type TMetricColumn = {
  key: string
  dataIndex?: string
  type?: string
  [key: string]: string | undefined
}

export const generalPostProcess = (
  data: TMetricItem[],
  columns: TMetricColumn[],
) => {
  const [dateColumn, ...dataColumns] = columns

  return (data ?? []).map((metrics: TMetricItem) => {
    const metric: Record<string, number | string | boolean> = {
      [dateColumn.key]: metrics[dateColumn.key],
    }
    dataColumns.forEach((column: TMetricColumn) => {
      const dataIndex = column.dataIndex ?? column.key
      const parsedValue = parseFloat(`${metrics[dataIndex]}`) ?? 0
      metric[dataIndex] =
        column.type === "number" ? parsedValue : metrics[dataIndex]
    })
    return metric
  })
}

export const MetricsMapping: TMetricsMap = {
  // Ad Forge queries
  ad_forge_adsets: {
    q: {
      dimensions: [
        "company_media_metrics_adsets.adset_id",
        "company_media_metrics_adsets.adset_name",
        "company_media_metrics_adsets.adset_status",
        "company_media_metrics_adsets.campaign_id",
        "company_media_metrics_adsets.campaign_name",
        "company_media_metrics_adsets.campaign_status",
        "company_media_metrics_adsets.media_channel",
        "company_media_metrics_adsets.objective",
        "company_media_metrics_adsets.optimization_goal",
      ],
      measures: [
        "company_media_metrics_adsets.budget_agg",
        "company_media_metrics_adsets.spend",
        "company_media_metrics_adsets.brand_value_agg",
      ],
      timeDimensions: [
        {
          dimension: "company_media_metrics_adsets.md_date",
        },
      ],
    },
  },
  ad_forge_campaigns: {
    q: {
      dimensions: [
        "company_media_metrics_campaigns.campaign_id",
        "company_media_metrics_campaigns.campaign_name",
        "company_media_metrics_campaigns.campaign_status",
      ],
      measures: ["company_media_metrics_campaigns.spend"],
      timeDimensions: [
        {
          dimension: "company_media_metrics_campaigns.md_date",
        },
      ],
    },
  },
  google_keywords_share_of_search_total_composed_chart: {
    q: {
      measures: [
        "company_mind_share_metrics_monthly.share_of_search",
        "company_mind_share_metrics_monthly.searches",
      ],
      dimensions: [
        "company_mind_share_metrics_monthly.brand",
        "company_mind_share_metrics_monthly.category",
      ],
      timeDimensions: [
        {
          dimension: "company_mind_share_metrics_monthly.date",
        },
      ],
      filters: [
        {
          dimension: "company_mind_share_metrics_monthly.metric",
          operator: "equals",
          values: ["google_keywords_share_of_search_total"],
        },
      ],
    },
    postProcess: (
      data: MetricDataItem[],
      params: { granularity?: TimeDimensionGranularity },
    ) => {
      const { granularity } = params ?? {
        granularity: GranularityMapping.MONTHLY,
      }
      const dateKey = `company_mind_share_metrics_monthly.date.${granularity}`
      const brandKey = "company_mind_share_metrics_monthly.brand"
      const absValueKey = "company_mind_share_metrics_monthly.searches"
      const resultMap: Record<string, MetricDataItem> = {}
      const columnsMap: Record<string, ColumnConfig> = {}

      const processPeriod = (
        item: MetricDataItem,
        prefix: "" | "compared.",
      ) => {
        const pDateKey = `${prefix}${dateKey}`
        if (!item[pDateKey]) return // No data for this period in this row

        // Use the main date as the primary key for the result map to align data points
        const primaryDate = dayjs(
          item[dateKey] as string | number | Date,
        ).format("YYYY-MM-DD")
        if (primaryDate === "Invalid Date") return

        if (!resultMap[primaryDate]) {
          resultMap[primaryDate] = { [dateKey]: primaryDate }
        }

        const pBrandKey = `${prefix}${brandKey}`
        const pAbsValueKey = `${prefix}${absValueKey}`
        const brandName = item[pBrandKey]
        const searches = parseFloat(item[pAbsValueKey] as string)

        if (isNaN(searches) || !brandName) return

        const periodKey = prefix ? "comparedBrands" : "brands"
        if (!resultMap[primaryDate][periodKey]) {
          resultMap[primaryDate][periodKey] = {}
        }

        const periodData = resultMap[primaryDate][periodKey] as Record<
          string,
          BrandData
        >
        if (periodData[brandName as string]) {
          periodData[brandName as string].searches += searches
        } else {
          periodData[brandName as string] = { searches }
        }
      }

      data.forEach((item: MetricDataItem) => {
        processPeriod(item, "") // Main data
        processPeriod(item, "compared.") // Compared data
      })

      const result = Object.values(resultMap)
        .sort(
          (a: MetricDataItem, b: MetricDataItem) =>
            dayjs(a[dateKey] as string | number | Date).unix() -
            dayjs(b[dateKey] as string | number | Date).unix(),
        )
        .map((dateEntry: MetricDataItem) => {
          const newItem: ProcessedMetricItem = { [dateKey]: dateEntry[dateKey] }

          const processAndFlatten = (
            brands: Record<string, { searches: number }>,
            prefix: "" | "compared.",
          ) => {
            if (!brands) return
            const totalSearches = Object.values(brands).reduce(
              (acc: number, brandData: BrandData) => acc + brandData.searches,
              0,
            )
            newItem[`${prefix}${TOTAL_CALCULATED_DATA_KEY}`] = totalSearches

            Object.keys(brands).forEach(brandName => {
              const brandData = brands[brandName]
              const share =
                totalSearches > 0
                  ? (brandData.searches / totalSearches) * 100
                  : 0
              const brandDataKey = `${prefix}${brandKey}.${brandName}`
              newItem[brandDataKey] = {
                searches: brandData.searches,
                share_of_search: share,
              }

              if (!prefix && !columnsMap[brandDataKey]) {
                columnsMap[brandDataKey] = {
                  dataIndex: brandDataKey,
                  key: brandDataKey,
                  shortTitle: brandName,
                  title: brandName,
                  type: "number",
                }
              }
            })
          }

          processAndFlatten(dateEntry.brands as Record<string, BrandData>, "")
          processAndFlatten(
            dateEntry.comparedBrands as Record<string, BrandData>,
            "compared.",
          )

          // The 'brandKey' is now a placeholder for the filter function to use.
          newItem[brandKey] = {}
          if (dateEntry.comparedBrands) {
            newItem[`compared.${brandKey}`] = {}
          }

          return newItem
        })

      return [
        result,
        [
          {
            dataIndex: dateKey,
            key: dateKey,
            shortTitle: "Date",
            title: "Company Mind Share Metrics Monthly Date",
            type: "time",
          },
          ...Object.values(columnsMap),
        ],
      ]
    },
    filter: (
      data: MetricDataItem[],
      granularity: TimeDimensionGranularity,
      options,
    ) => {
      const { currentCompany, compareMode } = options as {
        currentCompany?: CompanyDto
        compareMode?: boolean
      }
      const dateKey = `company_mind_share_metrics_monthly.date.${granularity}`
      const brandKey = "company_mind_share_metrics_monthly.brand"
      const { keywords } = (currentCompany?.metadata ?? {}) as {
        keywords: CompanyKeywords
      }
      const brands = keywords?.google?.competitors ?? []
      const alias = keywords?.google?.searchAlias
      const googleBrandAliases = keywords?.google?.brands
      const currentCompanyName = currentCompany?.name

      if (!brands || brands.length < 1 || !currentCompanyName) {
        return [] // Not configured, return empty data
      }

      const lowerBrands = brands.map((b: string) => b.toLowerCase())
      const lowerAliases = [
        currentCompanyName,
        ...(alias ? [alias] : []),
        ...(googleBrandAliases ?? []),
      ].map((a: string) => a.toLowerCase())

      const shareOfSearchSuffix = "share_of_search"
      const searchesSuffix = "searches"

      const groupAndFlatten = (
        dataItem: MetricDataItem,
        prefix: "" | "compared.",
        finalItem: ProcessedMetricItem,
      ) => {
        const pBrandKey = `${prefix}${brandKey}`
        const pUnknownKey = `${prefix}${UNKNOWN_DATA_KEY}`
        const allBrandKeysInData = Object.keys(dataItem).filter(k =>
          k.startsWith(`${pBrandKey}.`),
        )

        const tooltipGroup: Record<
          string,
          { searches: number; share_of_search: number }
        > = {}
        const unknownTooltipGroup: Record<
          string,
          { searches: number; share_of_search: number }
        > = {}
        let unknownSearches = 0
        let unknownShare = 0
        const foundCompetitors: string[] = []

        allBrandKeysInData.forEach(dataKey => {
          const brandName = dataKey.replace(`${pBrandKey}.`, "")
          const brandData = dataItem[dataKey]
          if (!brandData) {
            return
          }
          const lowerBrandName = brandName.toLowerCase()

          if (lowerAliases.includes(lowerBrandName)) {
            if (!tooltipGroup[currentCompanyName]) {
              tooltipGroup[currentCompanyName] = {
                searches: 0,
                share_of_search: 0,
              }
            }
            tooltipGroup[currentCompanyName].searches += (
              brandData as BrandData
            ).searches
            tooltipGroup[currentCompanyName].share_of_search +=
              (brandData as BrandData).share_of_search ?? 0
          } else if (lowerBrands.includes(lowerBrandName)) {
            tooltipGroup[brandName] = {
              searches: (brandData as BrandData).searches,
              share_of_search: (brandData as BrandData).share_of_search ?? 0,
            }
            foundCompetitors.push(lowerBrandName)
          } else {
            unknownTooltipGroup[brandName] = {
              searches: (brandData as BrandData).searches,
              share_of_search: (brandData as BrandData).share_of_search ?? 0,
            }
            unknownSearches += (brandData as BrandData).searches
            unknownShare += (brandData as BrandData).share_of_search ?? 0
          }
        })

        brands.forEach((brandName: string) => {
          if (!foundCompetitors.includes(brandName.toLowerCase())) {
            tooltipGroup[brandName] = { searches: 0, share_of_search: 0 }
          }
        })

        finalItem[pBrandKey] = tooltipGroup
        const unknownGroup = finalItem[pUnknownKey] as Record<
          string,
          unknown
        > & { searches: number; share_of_search: number }
        unknownGroup[brandKey] = unknownTooltipGroup
        unknownGroup.searches = unknownSearches
        unknownGroup.share_of_search = unknownShare

        Object.keys(tooltipGroup).forEach((brandName: string) => {
          const chartKey = `${pBrandKey}.${brandName}`
          finalItem[`${chartKey}.${shareOfSearchSuffix}`] =
            tooltipGroup[brandName].share_of_search
          finalItem[`${chartKey}.${searchesSuffix}`] =
            tooltipGroup[brandName].searches
        })

        const unknownChartKey = `${pBrandKey}.${UNKNOWN_DATA_KEY}`
        finalItem[`${unknownChartKey}.${shareOfSearchSuffix}`] = unknownShare
        finalItem[`${unknownChartKey}.${searchesSuffix}`] = unknownSearches
      }

      return data.map(dataItem => {
        const processedItem: ProcessedMetricItem = {
          [dateKey]: dataItem[dateKey],
          date: dataItem[dateKey],
          [TOTAL_CALCULATED_DATA_KEY]: dataItem[TOTAL_CALCULATED_DATA_KEY],
          [UNKNOWN_DATA_KEY]: {
            [brandKey]: {},
            searches: 0,
            share_of_search: 0,
          },
        }

        groupAndFlatten(dataItem, "", processedItem)

        if (compareMode) {
          processedItem[`compared.${TOTAL_CALCULATED_DATA_KEY}`] =
            dataItem[`compared.${TOTAL_CALCULATED_DATA_KEY}`]
          processedItem[`compared.${UNKNOWN_DATA_KEY}`] = {
            [brandKey]: {},
            searches: 0,
            share_of_search: 0,
          }
          groupAndFlatten(dataItem, "compared.", processedItem)
        }

        return processedItem
      })
    },
  },
  // No Category
  google_keywords_share_of_search_brands_share_composed_chart: {
    q: {
      measures: [
        "company_mind_share_metrics_monthly.share_of_search",
        "company_mind_share_metrics_monthly.searches",
      ],
      dimensions: ["company_mind_share_metrics_monthly.brand"],
      timeDimensions: [
        {
          dimension: "company_mind_share_metrics_monthly.date",
        },
      ],
      filters: [
        {
          dimension: "company_mind_share_metrics_monthly.metric",
          operator: "equals",
          values: ["google_keywords_share_of_search_per_brand"],
        },
      ],
    },
    postProcess: (
      data: MetricDataItem[],
      params: { granularity?: TimeDimensionGranularity },
    ) => {
      const { granularity } = params ?? {
        granularity: GranularityMapping.MONTHLY,
      }
      const dateKey = `company_mind_share_metrics_monthly.date.${granularity}`
      const brandKey = "company_mind_share_metrics_monthly.brand"
      const absValueKey = "company_mind_share_metrics_monthly.searches"
      const resultMap: Record<string, MetricDataItem> = {}
      const columnsMap: Record<string, ColumnConfig> = {}

      const processPeriod = (
        item: MetricDataItem,
        prefix: "" | "compared.",
      ) => {
        const pDateKey = `${prefix}${dateKey}`
        if (!item[pDateKey]) return // No data for this period in this row

        // Use the main date as the primary key for the result map to align data points
        const primaryDate = dayjs(
          item[dateKey] as string | number | Date,
        ).format("YYYY-MM-DD")
        if (primaryDate === "Invalid Date") return

        if (!resultMap[primaryDate]) {
          resultMap[primaryDate] = { [dateKey]: primaryDate }
        }

        const pBrandKey = `${prefix}${brandKey}`
        const pAbsValueKey = `${prefix}${absValueKey}`
        const brandName = item[pBrandKey]
        const searches = parseFloat(item[pAbsValueKey] as string)

        if (isNaN(searches) || !brandName) return

        const periodKey = prefix ? "comparedBrands" : "brands"
        if (!resultMap[primaryDate][periodKey]) {
          resultMap[primaryDate][periodKey] = {}
        }

        const periodData = resultMap[primaryDate][periodKey] as Record<
          string,
          BrandData
        >
        if (periodData[brandName as string]) {
          periodData[brandName as string].searches += searches
        } else {
          periodData[brandName as string] = { searches }
        }
      }

      data.forEach((item: MetricDataItem) => {
        processPeriod(item, "") // Main data
        processPeriod(item, "compared.") // Compared data
      })

      const result = Object.values(resultMap)
        .sort(
          (a: MetricDataItem, b: MetricDataItem) =>
            dayjs(a[dateKey] as string | number | Date).unix() -
            dayjs(b[dateKey] as string | number | Date).unix(),
        )
        .map((dateEntry: MetricDataItem) => {
          const newItem: ProcessedMetricItem = { [dateKey]: dateEntry[dateKey] }

          const processAndFlatten = (
            brands: Record<string, { searches: number }>,
            prefix: "" | "compared.",
          ) => {
            if (!brands) return
            const totalSearches = Object.values(brands).reduce(
              (acc: number, brandData: BrandData) => acc + brandData.searches,
              0,
            )
            newItem[`${prefix}${TOTAL_CALCULATED_DATA_KEY}`] = totalSearches

            Object.keys(brands).forEach(brandName => {
              const brandData = brands[brandName]
              const share =
                totalSearches > 0
                  ? (brandData.searches / totalSearches) * 100
                  : 0
              const brandDataKey = `${prefix}${brandKey}.${brandName}`
              newItem[brandDataKey] = {
                searches: brandData.searches,
                share_of_search: share,
              }

              if (!prefix && !columnsMap[brandDataKey]) {
                columnsMap[brandDataKey] = {
                  dataIndex: brandDataKey,
                  key: brandDataKey,
                  shortTitle: brandName,
                  title: brandName,
                  type: "number",
                }
              }
            })
          }

          processAndFlatten(dateEntry.brands as Record<string, BrandData>, "")
          processAndFlatten(
            dateEntry.comparedBrands as Record<string, BrandData>,
            "compared.",
          )

          // The 'brandKey' is now a placeholder for the filter function to use.
          newItem[brandKey] = {}
          if (dateEntry.comparedBrands) {
            newItem[`compared.${brandKey}`] = {}
          }
          return newItem
        })

      return [
        result,
        [
          {
            dataIndex: dateKey,
            key: dateKey,
            shortTitle: "Date",
            title: "Company Mind Share Metrics Monthly Date",
            type: "time",
          },
          ...Object.values(columnsMap),
        ],
      ]
    },
    filter: (
      data: MetricDataItem[],
      granularity: TimeDimensionGranularity,
      options,
    ) => {
      const { currentCompany, compareMode } = options as {
        currentCompany?: CompanyDto
        compareMode?: boolean
      }
      const dateKey = `company_mind_share_metrics_monthly.date.${granularity}`
      const brandKey = "company_mind_share_metrics_monthly.brand"
      const { keywords } = (currentCompany?.metadata ?? {}) as {
        keywords: CompanyKeywords
      }
      const brands = keywords?.google?.competitors ?? []
      const alias = keywords?.google?.searchAlias
      const googleBrandAliases = keywords?.google?.brands
      const currentCompanyName = currentCompany?.name

      if (!brands || brands.length < 1 || !currentCompanyName) {
        return [] // Not configured, return empty data
      }

      const lowerBrands = brands.map((b: string) => b.toLowerCase())
      const lowerAliases = [
        currentCompanyName,
        ...(alias ? [alias] : []),
        ...(googleBrandAliases ?? []),
      ].map((a: string) => a.toLowerCase())

      const shareOfSearchSuffix = "share_of_search"
      const searchesSuffix = "searches"

      const groupAndFlatten = (
        dataItem: MetricDataItem,
        prefix: "" | "compared.",
        finalItem: ProcessedMetricItem,
      ) => {
        const pBrandKey = `${prefix}${brandKey}`
        const pUnknownKey = `${prefix}${UNKNOWN_DATA_KEY}`
        const allBrandKeysInData = Object.keys(dataItem).filter(k =>
          k.startsWith(`${pBrandKey}.`),
        )

        const tooltipGroup: Record<
          string,
          { searches: number; share_of_search: number }
        > = {}
        const unknownTooltipGroup: Record<
          string,
          { searches: number; share_of_search: number }
        > = {}
        let unknownSearches = 0
        let unknownShare = 0
        const foundCompetitors: string[] = []

        allBrandKeysInData.forEach(dataKey => {
          const brandName = dataKey.replace(`${pBrandKey}.`, "")
          const brandData = dataItem[dataKey]
          if (!brandData) {
            return
          }
          const lowerBrandName = brandName.toLowerCase()

          if (lowerAliases.includes(lowerBrandName)) {
            if (!tooltipGroup[currentCompanyName]) {
              tooltipGroup[currentCompanyName] = {
                searches: 0,
                share_of_search: 0,
              }
            }
            tooltipGroup[currentCompanyName].searches += (
              brandData as BrandData
            ).searches
            tooltipGroup[currentCompanyName].share_of_search +=
              (brandData as BrandData).share_of_search ?? 0
          } else if (lowerBrands.includes(lowerBrandName)) {
            tooltipGroup[brandName] = {
              searches: (brandData as BrandData).searches,
              share_of_search: (brandData as BrandData).share_of_search ?? 0,
            }
            foundCompetitors.push(lowerBrandName)
          } else {
            unknownTooltipGroup[brandName] = {
              searches: (brandData as BrandData).searches,
              share_of_search: (brandData as BrandData).share_of_search ?? 0,
            }
            unknownSearches += (brandData as BrandData).searches
            unknownShare += (brandData as BrandData).share_of_search ?? 0
          }
        })

        brands.forEach((brandName: string) => {
          if (!foundCompetitors.includes(brandName.toLowerCase())) {
            tooltipGroup[brandName] = { searches: 0, share_of_search: 0 }
          }
        })

        finalItem[pBrandKey] = tooltipGroup
        const unknownGroup = finalItem[pUnknownKey] as Record<
          string,
          unknown
        > & { searches: number; share_of_search: number }
        unknownGroup[brandKey] = unknownTooltipGroup
        unknownGroup.searches = unknownSearches
        unknownGroup.share_of_search = unknownShare

        Object.keys(tooltipGroup).forEach((brandName: string) => {
          const chartKey = `${pBrandKey}.${brandName}`
          finalItem[`${chartKey}.${shareOfSearchSuffix}`] =
            tooltipGroup[brandName].share_of_search
          finalItem[`${chartKey}.${searchesSuffix}`] =
            tooltipGroup[brandName].searches
        })

        const unknownChartKey = `${pBrandKey}.${UNKNOWN_DATA_KEY}`
        finalItem[`${unknownChartKey}.${shareOfSearchSuffix}`] = unknownShare
        finalItem[`${unknownChartKey}.${searchesSuffix}`] = unknownSearches
      }

      return data.map(dataItem => {
        const processedItem: ProcessedMetricItem = {
          [dateKey]: dataItem[dateKey],
          date: dataItem[dateKey],
          [TOTAL_CALCULATED_DATA_KEY]: dataItem[TOTAL_CALCULATED_DATA_KEY],
          [UNKNOWN_DATA_KEY]: {
            [brandKey]: {},
            searches: 0,
            share_of_search: 0,
          },
        }

        groupAndFlatten(dataItem, "", processedItem)

        if (compareMode) {
          processedItem[`compared.${TOTAL_CALCULATED_DATA_KEY}`] =
            dataItem[`compared.${TOTAL_CALCULATED_DATA_KEY}`]
          processedItem[`compared.${UNKNOWN_DATA_KEY}`] = {
            [brandKey]: {},
            searches: 0,
            share_of_search: 0,
          }
          groupAndFlatten(dataItem, "compared.", processedItem)
        }

        return processedItem
      })
    },
  },
  // Category: <Category name>
  google_keywords_share_of_search_by_category_multi_chart: {
    q: {
      measures: [
        "company_mind_share_metrics_monthly.share_of_search",
        "company_mind_share_metrics_monthly.searches",
      ],
      dimensions: [
        "company_mind_share_metrics_monthly.category",
        "company_mind_share_metrics_monthly.brand",
      ],
      timeDimensions: [
        {
          dimension: "company_mind_share_metrics_monthly.date",
        },
      ],
      filters: [
        {
          dimension: "company_mind_share_metrics_monthly.metric",
          operator: "equals",
          values: ["google_keywords_share_of_search_per_category"],
        },
      ],
    },
    postProcess: (
      data: MetricDataItem[],
      params: { granularity?: TimeDimensionGranularity },
    ) => {
      const { granularity } = params ?? {
        granularity: GranularityMapping.MONTHLY,
      }
      const dateKey = `company_mind_share_metrics_monthly.date.${granularity}`
      const categoryKey = "company_mind_share_metrics_monthly.category"
      const brandKey = "company_mind_share_metrics_monthly.brand"
      const absValueKey = "company_mind_share_metrics_monthly.searches"
      const resultMap: Record<string, MetricDataItem> = {}
      const columnsMap: Record<string, ColumnConfig> = {}

      const processPeriod = (
        item: MetricDataItem,
        prefix: "" | "compared.",
      ) => {
        const pDateKey = `${prefix}${dateKey}`
        if (!item[pDateKey]) return

        const primaryDate = dayjs(
          item[dateKey] as string | number | Date,
        ).format("YYYY-MM-DD")
        if (primaryDate === "Invalid Date") return

        if (!resultMap[primaryDate]) {
          resultMap[primaryDate] = { [dateKey]: primaryDate }
        }

        const pCategoryKey = `${prefix}${categoryKey}`
        const pBrandKey = `${prefix}${brandKey}`
        const pAbsValueKey = `${prefix}${absValueKey}`

        const categoryName = item[pCategoryKey] as string
        const brandName = item[pBrandKey] as string
        const searches = parseFloat(item[pAbsValueKey] as string)

        if (isNaN(searches) || !brandName || !categoryName) return

        const periodKey = prefix ? "comparedCategories" : "categories"
        if (!resultMap[primaryDate][periodKey]) {
          resultMap[primaryDate][periodKey] = {}
        }
        const periodData = resultMap[primaryDate][periodKey] as Record<
          string,
          Record<string, BrandData>
        >
        if (!periodData[categoryName]) {
          periodData[categoryName] = {}
        }

        if (periodData[categoryName][brandName]) {
          periodData[categoryName][brandName].searches += searches
        } else {
          periodData[categoryName][brandName] = {
            searches,
          }
        }
      }

      data.forEach((item: MetricDataItem) => {
        processPeriod(item, "")
        processPeriod(item, "compared.")
      })

      const result = Object.values(resultMap)
        .sort(
          (a: MetricDataItem, b: MetricDataItem) =>
            dayjs(a[dateKey] as string | number | Date).unix() -
            dayjs(b[dateKey] as string | number | Date).unix(),
        )
        .map((dateEntry: MetricDataItem) => {
          const newItem: ProcessedMetricItem = { [dateKey]: dateEntry[dateKey] }

          const processAndFlatten = (
            categories: Record<string, Record<string, { searches: number }>>,
            prefix: "" | "compared.",
          ) => {
            if (!categories) return

            Object.keys(categories).forEach(categoryName => {
              const brands = categories[categoryName]
              const totalSearches = Object.values(brands).reduce(
                (acc: number, brandData: BrandData) => acc + brandData.searches,
                0,
              )

              newItem[`${prefix}${TOTAL_CALCULATED_DATA_KEY}`] = totalSearches

              Object.keys(brands).forEach(brandName => {
                const brandData = brands[brandName]
                const share =
                  totalSearches > 0
                    ? (brandData.searches / totalSearches) * 100
                    : 0
                const dataKey = `${prefix}${categoryName}.${brandName}`
                newItem[dataKey] = {
                  searches: brandData.searches,
                  share_of_search: share,
                }

                if (!prefix && !columnsMap[dataKey]) {
                  columnsMap[dataKey] = {
                    dataIndex: dataKey,
                    key: dataKey,
                    shortTitle: brandName,
                    title: `${categoryName} - ${brandName}`,
                    type: "number",
                  }
                }
              })
            })
          }
          processAndFlatten(
            dateEntry.categories as Record<string, Record<string, BrandData>>,
            "",
          )
          processAndFlatten(
            dateEntry.comparedCategories as Record<
              string,
              Record<string, BrandData>
            >,
            "compared.",
          )

          return newItem
        })

      return [
        result,
        [
          {
            dataIndex: dateKey,
            key: dateKey,
            shortTitle: "Date",
            title: "Company Mind Share Metrics Monthly Date",
            type: "time",
          },
          ...Object.values(columnsMap),
        ],
      ]
    },
    filter: (
      data: MetricDataItem[],
      granularity: TimeDimensionGranularity,
      options,
    ) => {
      const { currentCompany, compareMode } = options as {
        currentCompany?: CompanyDto
        compareMode?: boolean
      }
      const dateKey = `company_mind_share_metrics_monthly.date.${granularity}`
      const { keywords } = (currentCompany?.metadata ?? {}) as {
        keywords: CompanyKeywords
      }
      const categories = keywords?.google?.categories ?? []
      const brands = keywords?.google?.competitors ?? []
      const alias = keywords?.google?.searchAlias
      const googleBrandAliases = keywords?.google?.brands
      const currentCompanyName = currentCompany?.name

      if (categories.length < 1 || !currentCompanyName) {
        return { metrics: [], metricsConfig: [] }
      }

      const lowerBrands = brands.map((b: string) => b.toLowerCase())
      const lowerAliases = [
        currentCompanyName,
        ...(alias ? [alias] : []),
        ...(googleBrandAliases ?? []),
      ].map((a: string) => a.toLowerCase())

      const shareOfSearchSuffix = "share_of_search"
      const searchesSuffix = "searches"

      const unknownMetrics = new Set<string>()

      const metrics = data.map(dataItem => {
        const processedItem: ProcessedMetricItem = {
          [dateKey]: dataItem[dateKey],
          date: dataItem[dateKey],
        }

        const groupPeriodData = (prefix: "" | "compared.") => {
          processedItem[`${prefix}${UNKNOWN_DATA_KEY}`] = {}

          categories.forEach((category: string) => {
            const lowerCategory = category.toLowerCase()
            let companyShare = 0
            let companySearches = 0
            let unknownShare = 0
            let unknownSearches = 0

            const unknownTooltipGroup: TooltipGroup = {}
            const categoryTooltipGroup: Record<
              string,
              | {
                  searches: number
                  share_of_search: number
                  brands?: TooltipGroup
                }
              | number
            > = {
              [TOTAL_CALCULATED_DATA_KEY]: 0,
            }

            const allDataKeysForPeriod = Object.keys(dataItem).filter(k =>
              k.startsWith(prefix),
            )

            // First pass: Calculate totals
            allDataKeysForPeriod.forEach(dataKey => {
              const keyWithoutPrefix = dataKey.substring(prefix.length)
              const [keyCategory, brandName] = keyWithoutPrefix.split(/\.(.*)/s)

              if (keyCategory.toLowerCase() !== lowerCategory || !brandName) {
                return
              }

              const brandData = dataItem[dataKey] as BrandData
              if (!brandData || !brandData.searches) return
              ;(categoryTooltipGroup[TOTAL_CALCULATED_DATA_KEY] as number) +=
                parseFloat(String(brandData.searches))
            })

            // Second pass: Process individual brands
            allDataKeysForPeriod.forEach(dataKey => {
              const keyWithoutPrefix = dataKey.substring(prefix.length)
              const [keyCategory, brandName] = keyWithoutPrefix.split(/\.(.*)/s)

              if (keyCategory.toLowerCase() !== lowerCategory || !brandName) {
                return
              }

              const brandData = dataItem[dataKey] as BrandData
              if (!brandData) return

              const lowerBrandName = brandName.toLowerCase()
              const totalSearches = categoryTooltipGroup[
                TOTAL_CALCULATED_DATA_KEY
              ] as number

              if (lowerAliases.includes(lowerBrandName)) {
                const share =
                  totalSearches > 0
                    ? (brandData.searches / totalSearches) * 100
                    : 0
                companyShare += share
                companySearches += brandData.searches
              } else if (lowerBrands.includes(lowerBrandName)) {
                const share =
                  totalSearches > 0
                    ? (brandData.searches / totalSearches) * 100
                    : 0
                processedItem[
                  `${prefix}${category}.${brandName}.${shareOfSearchSuffix}`
                ] = share
                processedItem[
                  `${prefix}${category}.${brandName}.${searchesSuffix}`
                ] = brandData.searches
                categoryTooltipGroup[brandName] = {
                  searches: brandData.searches,
                  share_of_search: share,
                }
              } else {
                const share =
                  totalSearches > 0
                    ? (brandData.searches / totalSearches) * 100
                    : 0
                unknownShare += share
                unknownSearches += brandData.searches
                unknownTooltipGroup[brandName] = {
                  searches: brandData.searches,
                  share_of_search: share,
                }
                if (!prefix) unknownMetrics.add(brandName)
              }
            })

            // Store total calculated data
            processedItem[`${prefix}${category}.${TOTAL_CALCULATED_DATA_KEY}`] =
              categoryTooltipGroup[TOTAL_CALCULATED_DATA_KEY]

            // Store company data
            processedItem[
              `${prefix}${category}.${currentCompanyName}.${shareOfSearchSuffix}`
            ] = companyShare
            processedItem[
              `${prefix}${category}.${currentCompanyName}.${searchesSuffix}`
            ] = companySearches
            categoryTooltipGroup[currentCompanyName] = {
              searches: companySearches,
              share_of_search: companyShare,
            }

            // Store unknown data
            processedItem[
              `${prefix}${category}.${UNKNOWN_DATA_KEY}.${shareOfSearchSuffix}`
            ] = unknownShare
            processedItem[
              `${prefix}${category}.${UNKNOWN_DATA_KEY}.${searchesSuffix}`
            ] = unknownSearches
            categoryTooltipGroup[UNKNOWN_DATA_KEY] = {
              searches: unknownSearches,
              share_of_search: unknownShare,
              brands: unknownTooltipGroup,
            }

            processedItem[`${prefix}${category}`] = categoryTooltipGroup
          })
        }

        groupPeriodData("")
        if (compareMode) {
          groupPeriodData("compared.")
        }
        return processedItem
      })

      const metricsConfig = {
        category: categories,
        brand: [currentCompanyName, ...brands],
        unknown: { brand: Array.from(unknownMetrics) },
        suffixKey: "share_of_search",
      }

      return {
        metrics,
        metricsConfig: metricsConfig as unknown as MetricsConfig,
      }
    },
  },
  instagram_share_of_comments_area_chart: {
    q: {
      measures: ["metrics.instagram_insights_share_of_comments"],
      filters: [
        {
          member: "metrics.metric",
          operator: "equals",
          values: ["instagram_insights_share_of_comments"],
        },
      ],
      order: {
        "metrics.date": "asc",
      },
      timeDimensions: [
        {
          dimension: "metrics.date",
        },
      ],
    },
  },
  instagram_share_of_followers_area_chart: {
    q: {
      measures: ["metrics.instagram_insights_share_of_followers"],
      filters: [],
      order: {
        "metrics.date": "asc",
      },
      timeDimensions: [
        {
          dimension: "metrics.date",
        },
      ],
    },
  },
  instagram_share_of_likes_area_chart: {
    q: {
      measures: ["metrics.instagram_insights_share_of_likes"],
      filters: [],
      order: {
        "metrics.date": "asc",
      },
      timeDimensions: [
        {
          dimension: "metrics.date",
        },
      ],
    },
  },
  total_social_profile_views_stacked_chart: {
    q: {
      measures: [
        "metrics.instagram_business_profile_views",
        "metrics.tiktok_profile_views",
        "metrics.facebook_pages_views",
        "metrics.total_social_profile_views",
      ],
      filters: [],
      order: {
        "metrics.date": "asc",
      },
      timeDimensions: [
        {
          dimension: "metrics.date",
        },
      ],
    },
  },
  total_shares_stacked_chart: {
    q: {
      measures: [
        "metrics.tiktok_videos_shares",
        "metrics.tiktok_ads_shares",
        "metrics.instagram_business_shares",
        "metrics.instagram_ads_shares",
        "metrics.facebook_pages_shares",
        "metrics.facebook_ads_shares",
        "metrics.youtube_channel_shares",
      ],
      filters: [],
      order: {
        "metrics.date": "asc",
      },
      timeDimensions: [
        {
          dimension: "metrics.date",
        },
      ],
    },
  },
  total_saves_stacked_chart: {
    q: {
      measures: [
        "metrics.instagram_business_saves",
        "metrics.instagram_ads_saves",
        "metrics.tiktok_ads_saves",
        "metrics.pinterest_ads_saves",
      ],
      filters: [],
      order: {
        "metrics.date": "asc",
      },
      timeDimensions: [
        {
          dimension: "metrics.date",
        },
      ],
    },
  },
  total_new_subscribers_stacked_chart: {
    q: {
      measures: [
        "metrics.attentive_sms_subscribers",
        "metrics.klaviyo_email_subscribers",
        "metrics.total_new_subscribers",
      ],
      filters: [],
      order: {
        "metrics.date": "asc",
      },
      timeDimensions: [
        {
          dimension: "metrics.date",
        },
      ],
    },
  },
  total_followers_stacked_chart: {
    q: {
      measures: [
        "metrics.tiktok_profile_follows",
        "metrics.tiktok_ads_follows",
        "metrics.facebook_pages_follows",
        "metrics.youtube_channel_follows",
        "metrics.total_new_followers",
        "metrics.facebook_ads_follows",
      ],
      filters: [],
      order: {
        "metrics.date": "asc",
      },
      timeDimensions: [
        {
          dimension: "metrics.date",
        },
      ],
    },
  },
  total_likes_stacked_chart: {
    q: {
      measures: [
        "metrics.instagram_business_likes",
        "metrics.instagram_ads_likes",
        "metrics.tiktok_videos_likes",
        "metrics.tiktok_ads_likes",
        "metrics.facebook_pages_likes",
        "metrics.facebook_ads_likes",
        "metrics.youtube_channel_likes",
        "metrics.total_likes",
      ],
      filters: [],
      order: {
        "metrics.date": "asc",
      },
      timeDimensions: [
        {
          dimension: "metrics.date",
        },
      ],
    },
  },
  total_comments_stacked_chart: {
    q: {
      measures: [
        "metrics.instagram_business_comments",
        "metrics.instagram_ads_comments",
        "metrics.tiktok_videos_comments",
        "metrics.tiktok_ads_comments",
        "metrics.facebook_pages_comments",
        "metrics.facebook_ads_comments",
        "metrics.youtube_channel_comments",
        "metrics.total_comments",
      ],
      filters: [],
      order: {
        "metrics.date": "asc",
      },
      timeDimensions: [
        {
          dimension: "metrics.date",
        },
      ],
    },
  },
  facebook_mentions_area_chart: {
    q: {
      measures: ["metrics.facebook_pages_mentions"],
      filters: [],
      order: {
        "metrics.date": "asc",
      },
      timeDimensions: [
        {
          dimension: "metrics.date",
        },
      ],
    },
  },
  branded_search_totals_stacked_chart: {
    q: {
      measures: [
        "metrics.google_search_organic_branded_search_impressions",
        "metrics.google_ads_paid_branded_search_impressions",
        "metrics.google_ads_paid_branded_search_total_spend",
        "metrics.google_search_organic_branded_search_clicks",
        "metrics.google_ads_paid_branded_search_clicks",
      ],
      dimensions: ["metrics.companyid"],
      filters: [
        {
          member: "metrics.metric",
          operator: "equals",
          values: [
            "google_search_organic_branded_search_impressions",
            "google_ads_paid_branded_search_impressions",
            "google_ads_paid_branded_search_total_spend",
            "google_search_organic_branded_search_clicks",
            "google_ads_paid_branded_search_clicks",
          ],
        },
      ],
      order: {
        "metrics.date": "asc",
      },
      timeDimensions: [
        {
          dimension: "metrics.date",
        },
      ],
    },
  },
  ga_revenue_break_down_stacked_chart: {
    q: {
      measures: [
        "metrics.ga_baseline_revenue",
        "metrics.ga_paid_revenue",
        "metrics.ga_triggered_revenue",
        "metrics.total_ad_spend",
      ],
      timeDimensions: [
        {
          dimension: "metrics.date",
        },
      ],
      order: {
        "metrics.date": "asc",
      },
    },
  },
  ga_mp_revenue_break_down_stacked_chart: {
    q: {
      measures: [
        "metrics.ga_multi_property_baseline_revenue",
        "metrics.ga_multi_property_paid_revenue",
        "metrics.ga_multi_property_triggered_revenue",
        "metrics.total_ad_spend",
      ],
      timeDimensions: [
        {
          dimension: "metrics.date",
        },
      ],
      order: {
        "metrics.date": "asc",
      },
    },
  },
  shopify_revenue_break_down_stacked_chart: {
    q: {
      measures: [
        "metrics.shopify_baseline_revenue",
        "metrics.shopify_paid_revenue",
        "metrics.shopify_triggered_revenue",
        "metrics.shopify_subscription_discount_revenue",
        "metrics.shopify_subscription_nondiscount_revenue",
        "metrics.total_ad_spend",
      ],
      timeDimensions: [
        {
          dimension: "metrics.date",
        },
      ],
      order: {
        "metrics.date": "asc",
      },
    },
  },
  shopify_revenue_with_discount_table: {
    q: {
      measures: [
        "metrics.shopify_baseline_revenue",
        "metrics.shopify_baseline_plus_paid_brand_search_revenue",
        "metrics.shopify_paid_revenue",
        "metrics.shopify_triggered_revenue",
        "metrics.shopify_baseline_discount_revenue",
        "metrics.shopify_paid_discount_revenue",
        "metrics.shopify_subscription_discount_revenue",
        "metrics.shopify_subscription_revenue",
        "metrics.shopify_subscription_nondiscount_revenue",
        "metrics.shopify_triggered_discount_revenue",
      ],
      timeDimensions: [
        {
          dimension: "metrics.date",
        },
      ],
      order: {
        "metrics.date": "asc",
      },
    },
    postProcess(data: MetricDataItem[]) {
      const processed = data.reduce(
        (acc, item) => ({
          shopify_baseline_revenue:
            +(acc.shopify_baseline_revenue as number) +
            +(item["metrics.shopify_baseline_revenue"] ?? 0),
          shopify_baseline_plus_paid_brand_search_revenue:
            +(acc.shopify_baseline_plus_paid_brand_search_revenue as number) +
            +(
              item["metrics.shopify_baseline_plus_paid_brand_search_revenue"] ??
              0
            ),
          shopify_paid_revenue:
            +(acc.shopify_paid_revenue as number) +
            +(item["metrics.shopify_paid_revenue"] ?? 0),
          shopify_triggered_revenue:
            +(acc.shopify_triggered_revenue as number) +
            +(item["metrics.shopify_triggered_revenue"] ?? 0),
          shopify_baseline_discount_revenue:
            +(acc.shopify_baseline_discount_revenue as number) +
            +(item["metrics.shopify_baseline_discount_revenue"] ?? 0),
          shopify_paid_discount_revenue:
            +(acc.shopify_paid_discount_revenue as number) +
            +(item["metrics.shopify_paid_discount_revenue"] ?? 0),
          shopify_triggered_discount_revenue:
            +(acc.shopify_triggered_discount_revenue as number) +
            +(item["metrics.shopify_triggered_discount_revenue"] ?? 0),
          shopify_subscription_discount_revenue:
            +(acc.shopify_subscription_discount_revenue as number) +
            +(item["metrics.shopify_subscription_discount_revenue"] ?? 0),
          shopify_subscription_non_discount_revenue:
            +(acc.shopify_subscription_non_discount_revenue as number) +
            +(item["metrics.shopify_subscription_nondiscount_revenue"] ?? 0),
          shopify_subscription_revenue:
            +(acc.shopify_subscription_revenue as number) +
            +(item["metrics.shopify_subscription_revenue"] ?? 0),
        }),
        {
          shopify_baseline_revenue: 0,
          shopify_baseline_plus_paid_brand_search_revenue: 0,
          shopify_paid_revenue: 0,
          shopify_triggered_revenue: 0,
          shopify_baseline_discount_revenue: 0,
          shopify_paid_discount_revenue: 0,
          shopify_triggered_discount_revenue: 0,
          shopify_subscription_revenue: 0,
          shopify_subscription_discount_revenue: 0,
          shopify_subscription_non_discount_revenue: 0,
        },
      )

      processed.shopify_baseline_non_discount_revenue =
        (processed.shopify_baseline_revenue as number)
          ? (processed.shopify_baseline_revenue as number) -
            (processed.shopify_baseline_discount_revenue as number)
          : 0
      processed.shopify_paid_non_discount_revenue =
        (processed.shopify_paid_revenue as number)
          ? (processed.shopify_paid_revenue as number) -
            (processed.shopify_paid_discount_revenue as number)
          : 0
      processed.shopify_triggered_non_discount_revenue =
        (processed.shopify_triggered_revenue as number)
          ? (processed.shopify_triggered_revenue as number) -
            (processed.shopify_triggered_discount_revenue as number)
          : 0

      const totalRevenue =
        ((processed.shopify_baseline_revenue as number) ?? 0) +
        (processed.shopify_paid_revenue as number) +
        (processed.shopify_triggered_revenue as number) +
        (processed.shopify_subscription_revenue as number)

      const result = Object.values(SHOPIFY_REVENUE_TYPE).reduce(
        (acc: ProcessedMetricItem[], revenueType) => [
          ...acc,
          {
            revenue_type: SHOPIFY_REVENUE_TYPE_TO_TITLE_MAP[revenueType],
            color: SHOPIFY_REVENUE_TYPE_TO_COLOR_MAP[revenueType],
            non_discount_revenue: processed[
              `shopify_${revenueType}_non_discount_revenue`
            ] as number,
            non_discount_revenue_of_type: safeDivide(
              processed[
                `shopify_${revenueType}_non_discount_revenue`
              ] as number,
              processed[`shopify_${revenueType}_revenue`] as number,
            ),
            non_discount_revenue_of_total: safeDivide(
              processed[
                `shopify_${revenueType}_non_discount_revenue`
              ] as number,
              totalRevenue,
            ),
            discount_revenue: processed[
              `shopify_${revenueType}_discount_revenue`
            ] as number,
            discount_revenue_of_type: safeDivide(
              processed[`shopify_${revenueType}_discount_revenue`] as number,
              processed[`shopify_${revenueType}_revenue`] as number,
            ),
            discount_revenue_of_total: safeDivide(
              processed[`shopify_${revenueType}_discount_revenue`] as number,
              totalRevenue,
            ),
            total_revenue: processed[
              `shopify_${revenueType}_revenue`
            ] as number,
            total_revenue_of_total: safeDivide(
              processed[`shopify_${revenueType}_revenue`] as number,
              totalRevenue,
            ),
          },
        ],
        [],
      )
      return [result]
    },
  },
  ga_session_paid_triggered_and_baseline_traffic_data_from_daily_metrics_table:
    {
      q: {
        measures: [
          "metrics.ga_baseline_revenue",
          "metrics.ga_paid_revenue",
          "metrics.ga_triggered_revenue",
          "metrics.ga_baseline_sessions",
          "metrics.ga_paid_sessions",
          "metrics.ga_triggered_sessions",
          "metrics.total_ad_spend",
        ],
        timeDimensions: [
          {
            dimension: "metrics.date",
          },
        ],
        order: {
          "metrics.date": "asc",
        },
      },
      postProcess(data: MetricDataItem[]) {
        return [
          data.map(dataItem => ({
            "company_revenue_metrics_daily.baseline_sessions":
              dataItem["metrics.ga_baseline_sessions"],
            "company_revenue_metrics_daily.baseline_revenue":
              dataItem["metrics.ga_baseline_revenue"],
            "company_revenue_metrics_daily.triggered_sessions":
              dataItem["metrics.ga_triggered_sessions"],
            "company_revenue_metrics_daily.triggered_revenue":
              dataItem["metrics.ga_triggered_revenue"],
            "company_revenue_metrics_daily.paid_sessions":
              dataItem["metrics.ga_paid_sessions"],
            "company_revenue_metrics_daily.paid_revenue":
              dataItem["metrics.ga_paid_revenue"],
            "company_revenue_metrics_daily.baseline_revenue_per_session":
              safeDivide(
                +(dataItem["metrics.ga_baseline_revenue"] as number),
                +(dataItem["metrics.ga_baseline_sessions"] as number),
              ),
            "company_revenue_metrics_daily.triggered_revenue_per_session":
              safeDivide(
                +(dataItem["metrics.ga_triggered_revenue"] as number),
                +(dataItem["metrics.ga_triggered_sessions"] as number),
              ),
            "company_revenue_metrics_daily.paid_revenue_per_session":
              safeDivide(
                +(dataItem["metrics.ga_paid_revenue"] as number),
                +(dataItem["metrics.ga_paid_sessions"] as number),
              ),
          })),
        ]
      },
    },
  // ToDo: don't delete, should be used after data is fixed
  // https://linear.app/marathon-data/issue/MD-3095/urgent-data-bug-google-analytics-revenue-page-mismatch-between-data
  ga_session_paid_triggered_and_baseline_traffic_data_table: {
    q: [
      {
        measures: [
          "company_revenue_metrics_daily.baseline_sessions",
          "company_revenue_metrics_daily.baseline_revenue",
        ],
        dimensions: ["company_revenue_metrics_daily.companyid"],
        timeDimensions: [
          {
            dimension: "company_revenue_metrics_daily.date",
          },
        ],
      },
      {
        measures: [
          "company_revenue_metrics_daily.triggered_sessions",
          "company_revenue_metrics_daily.triggered_revenue",
        ],
        dimensions: ["company_revenue_metrics_daily.companyid"],
        timeDimensions: [
          {
            dimension: "company_revenue_metrics_daily.date",
          },
        ],
      },
      {
        measures: [
          "company_revenue_metrics_daily.paid_sessions",
          "company_revenue_metrics_daily.paid_revenue",
        ],
        dimensions: ["company_revenue_metrics_daily.companyid"],
        timeDimensions: [
          {
            dimension: "company_revenue_metrics_daily.date",
          },
        ],
      },
    ],
    postProcess(data: MetricDataItem[]) {
      return [
        data.map(dataItem => ({
          ...dataItem,
          "company_revenue_metrics_daily.baseline_revenue_per_session":
            safeDivide(
              +(dataItem[
                "company_revenue_metrics_daily.baseline_revenue"
              ] as number),
              +(dataItem[
                "company_revenue_metrics_daily.baseline_sessions"
              ] as number),
            ),
          "company_revenue_metrics_daily.triggered_revenue_per_session":
            safeDivide(
              +(dataItem[
                "company_revenue_metrics_daily.triggered_revenue"
              ] as number),
              +(dataItem[
                "company_revenue_metrics_daily.triggered_sessions"
              ] as number),
            ),
          "company_revenue_metrics_daily.paid_revenue_per_session": safeDivide(
            +(dataItem["company_revenue_metrics_daily.paid_revenue"] as number),
            +(dataItem[
              "company_revenue_metrics_daily.paid_sessions"
            ] as number),
          ),
        })),
      ]
    },
  },
  ga_mp_session_paid_triggered_and_baseline_traffic_data_table: {
    q: {
      measures: [
        "metrics.ga_multi_property_baseline_sessions",
        "metrics.ga_multi_property_baseline_revenue",
        "metrics.ga_multi_property_triggered_sessions",
        "metrics.ga_multi_property_triggered_revenue",
        "metrics.ga_multi_property_paid_revenue",
        "metrics.ga_multi_property_paid_sessions",
      ],
      dimensions: ["metrics.companyid"],
      timeDimensions: [
        {
          dimension: "metrics.date",
        },
      ],
    },
    postProcess(data: MetricDataItem[]) {
      return [
        data.map(dataItem => ({
          ...dataItem,
          "metrics.ga_multi_property_baseline_revenue_per_session": safeDivide(
            +(dataItem["metrics.ga_multi_property_baseline_revenue"] as number),
            +(dataItem[
              "metrics.ga_multi_property_baseline_sessions"
            ] as number),
          ),
          "metrics.ga_multi_property_triggered_revenue_per_session": safeDivide(
            +(dataItem[
              "metrics.ga_multi_property_triggered_revenue"
            ] as number),
            +(dataItem[
              "metrics.ga_multi_property_triggered_sessions"
            ] as number),
          ),
          "metrics.ga_multi_property_paid_revenue_per_session": safeDivide(
            +(dataItem["metrics.ga_multi_property_paid_revenue"] as number),
            +(dataItem["metrics.ga_multi_property_paid_sessions"] as number),
          ),
        })),
      ]
    },
  },
  ga_baseline_traffic_vs_revenue_stacked_chart: {
    q: {
      measures: ["metrics.ga_baseline_revenue", "metrics.ga_baseline_sessions"],
      timeDimensions: [
        {
          dimension: "metrics.date",
        },
      ],
      order: {
        "metrics.date": "asc",
      },
    },
  },
  ga_mp_baseline_traffic_vs_revenue_stacked_chart: {
    q: {
      measures: [
        "metrics.ga_multi_property_baseline_revenue",
        "metrics.ga_multi_property_baseline_sessions",
      ],
      timeDimensions: [
        {
          dimension: "metrics.date",
        },
      ],
      order: {
        "metrics.date": "asc",
      },
    },
  },
  ga_sessions_from_organic_sessions: {
    q: {
      measures: [
        "metrics.ga_sessions_from_organic_sessions",
        "metrics.ga_revenue_from_organic_sessions",
      ],
      filters: [],
      order: {
        "metrics.date": "asc",
      },
      timeDimensions: [
        {
          dimension: "metrics.date",
        },
      ],
    },
  },
  ga_sessions_from_direct_sessions: {
    q: {
      measures: [
        "metrics.ga_sessions_from_direct_sessions",
        "metrics.ga_revenue_from_direct_sessions",
      ],
      filters: [],
      order: {
        "metrics.date": "asc",
      },
      timeDimensions: [
        {
          dimension: "metrics.date",
        },
      ],
    },
  },
  ga_sessions_from_organic_referal_sessions: {
    q: {
      measures: [
        "metrics.ga_sessions_from_organic_referal_sessions",
        "metrics.ga_revenue_from_organic_referral_sessions",
      ],
      filters: [],
      order: {
        "metrics.date": "asc",
      },
      timeDimensions: [
        {
          dimension: "metrics.date",
        },
      ],
    },
  },
  ga_mp_sessions_from_organic_sessions: {
    q: {
      measures: [
        "metrics.ga_multi_property_sessions_from_organic_sessions",
        "metrics.ga_multi_property_revenue_from_organic_sessions",
      ],
      filters: [],
      order: {
        "metrics.date": "asc",
      },
      timeDimensions: [
        {
          dimension: "metrics.date",
        },
      ],
    },
  },
  ga_mp_sessions_from_direct_sessions: {
    q: {
      measures: [
        "metrics.ga_multi_property_sessions_from_direct_sessions",
        "metrics.ga_multi_property_revenue_from_direct_sessions",
      ],
      filters: [],
      order: {
        "metrics.date": "asc",
      },
      timeDimensions: [
        {
          dimension: "metrics.date",
        },
      ],
    },
  },
  ga_mp_sessions_from_organic_referal_sessions: {
    q: {
      measures: [
        "metrics.ga_multi_property_sessions_from_organic_referal_sessions",
        "metrics.ga_multi_property_revenue_from_organic_referral_sessions",
      ],
      filters: [],
      order: {
        "metrics.date": "asc",
      },
      timeDimensions: [
        {
          dimension: "metrics.date",
        },
      ],
    },
  },
  session_source_and_medium_traffic_data_table: {
    q: {
      measures: [
        "company_revenue_metrics_daily.baseline_sessions",
        "company_revenue_metrics_daily.baseline_engaged_sessions",
        "company_revenue_metrics_daily.baseline_new_sessions",
        "company_revenue_metrics_daily.baseline_revenue",
      ],
      dimensions: [
        "company_revenue_metrics_daily.sessionsource",
        "company_revenue_metrics_daily.sessionmedium",
        "company_revenue_metrics_daily.companyid",
      ],
      timeDimensions: [
        {
          dimension: "company_revenue_metrics_daily.date",
        },
      ],
      filters: [
        {
          member: "company_revenue_metrics_daily.baseline_sessions",
          operator: "gt",
          values: ["0"],
        },
      ],
    },
    postProcess(data: MetricDataItem[]) {
      return [
        data.map(dataItem => ({
          ...dataItem,
          "company_revenue_metrics_daily.baseline_revenue_per_session":
            safeDivide(
              dataItem[
                "company_revenue_metrics_daily.baseline_revenue"
              ] as number,
              dataItem[
                "company_revenue_metrics_daily.baseline_sessions"
              ] as number,
            ),
        })),
      ]
    },
  },
  branded_search_clicks_stacked_chart: {
    q: {
      measures: [
        "metrics.google_search_organic_branded_search_clicks",
        "metrics.google_ads_paid_branded_search_clicks",
        "metrics.google_ads_paid_branded_search_total_spend",
      ],
      filters: [
        // TODO: Delete later if necessary
        {
          member: "metrics.metric",
          operator: "equals",
          values: [
            "google_search_organic_branded_search_clicks",
            "google_ads_paid_branded_search_clicks",
            "google_ads_paid_branded_search_total_spend",
          ],
        },
      ],
      order: {
        "metrics.date": "asc",
      },
      timeDimensions: [
        {
          dimension: "metrics.date",
        },
      ],
    },
  },
  brand_value_cohort_table: {
    q: {
      measures: [
        "company_modeled_metrics_monthly.engagements",
        "company_modeled_metrics_monthly.conversionMonth1",
        "company_modeled_metrics_monthly.conversionMonth2",
        "company_modeled_metrics_monthly.conversionMonth3",
        "company_modeled_metrics_monthly.conversionMonth4",
        "company_modeled_metrics_monthly.conversionMonth5",
        "company_modeled_metrics_monthly.conversionMonth6",
        // "company_modeled_metrics_monthly.conversionMonth7",
        // "company_modeled_metrics_monthly.conversionMonth8",
        // "company_modeled_metrics_monthly.conversionMonth9",
        // "company_modeled_metrics_monthly.conversionMonth10",
        // "company_modeled_metrics_monthly.conversionMonth11",
        // "company_modeled_metrics_monthly.conversionMonth12",
      ],
      filters: [],
      order: {
        "company_modeled_metrics_monthly.engagement_date": "asc",
      },
      timeDimensions: [
        {
          dimension: "company_modeled_metrics_monthly.engagement_date",
        },
      ],
    },
  },
  brand_value_conversions_by_channel_group_table: {
    q: (params: QueryParams) => {
      const { depVar = DEFAULT_DEP_VAR_METRIC, depVarScaler = [] } = params as {
        depVar?: string
        depVarScaler?: string[]
      }
      return {
        measures: [
          "company_platform_brand_value.engagements",
          "company_platform_brand_value.revenue30Days",
          "company_platform_brand_value.revenue180Days",
          // "company_platform_brand_value.revenuePerEngagement30Days",
          // "company_platform_brand_value.revenuePerEngagement180Days",
        ],
        dimensions: ["company_platform_brand_value.channel_group"],
        filters: [
          {
            and: [
              {
                dimension: "company_platform_brand_value.dep_var",
                operator: "equals",
                values: [depVar],
              },
              depVarScaler?.length > 0
                ? {
                    dimension: "company_platform_brand_value.dep_var_scaler",
                    operator: "equals",
                    values: depVarScaler,
                  }
                : undefined,
            ].filter(f => !!f) as Filter[],
          },
        ],
        timeDimensions: [
          {
            dimension: "company_platform_brand_value.engagement_date",
          },
        ],
      }
    },
  },
  brand_value_conversions_by_channel_group_table_summary: {
    q: (params: QueryParams) => {
      const { depVar = DEFAULT_DEP_VAR_METRIC, depVarScaler = [] } = params as {
        depVar?: string
        depVarScaler?: string[]
      }
      return {
        measures: [
          "company_platform_brand_value.engagements",
          "company_platform_brand_value.revenue30Days",
          "company_platform_brand_value.revenue180Days",
          // "company_platform_brand_value.revenuePerEngagement30Days",
          // "company_platform_brand_value.revenuePerEngagement180Days",
          "company_platform_brand_value.channel_groups_count",
        ],
        timeDimensions: [
          {
            dimension: "company_platform_brand_value.engagement_date",
          },
        ],
        filters: [
          {
            and: [
              {
                dimension: "company_platform_brand_value.dep_var",
                operator: "equals",
                values: [depVar],
              },
              depVarScaler.length > 0
                ? {
                    dimension: "company_platform_brand_value.dep_var_scaler",
                    operator: "equals",
                    values: depVarScaler,
                  }
                : undefined,
            ].filter(f => !!f) as Filter[],
          },
        ],
      }
    },
    postProcess: (data: MetricDataItem[]) => {
      const requiredFields = [
        // "company_platform_brand_value.engagements",
        "company_platform_brand_value.revenue30Days",
        "company_platform_brand_value.revenue180Days",
      ]

      return [
        data.map((metric: MetricDataItem) => {
          const res = {
            ...metric,
          }
          requiredFields.forEach((field: string) => {
            res[`${field}_count`] =
              metric["company_platform_brand_value.channel_groups_count"]
          })
          return res
        }),
        requiredFields,
      ]
    },
  },
  brand_value_conversions_by_channel_table: {
    q: (params: QueryParams) => {
      const { depVar = DEFAULT_DEP_VAR_METRIC, depVarScaler = [] } = params as {
        depVar?: string
        depVarScaler?: string[]
      }
      return {
        measures: [
          "company_platform_brand_value.engagements",
          "company_platform_brand_value.revenue30Days",
          "company_platform_brand_value.revenue180Days",
          // "company_platform_brand_value.revenuePerEngagement30Days",
          // "company_platform_brand_value.revenuePerEngagement180Days",
        ],
        dimensions: ["company_platform_brand_value.channel"],
        filters: [
          {
            and: [
              {
                dimension: "company_platform_brand_value.dep_var",
                operator: "equals",
                values: [depVar],
              },
              depVarScaler.length > 0
                ? {
                    dimension: "company_platform_brand_value.dep_var_scaler",
                    operator: "equals",
                    values: depVarScaler,
                  }
                : undefined,
            ].filter(f => !!f) as Filter[],
          },
        ],
        timeDimensions: [
          {
            dimension: "company_platform_brand_value.engagement_date",
          },
        ],
      }
    },
  },
  brand_value_conversions_by_channel_table_summary: {
    q: (params: QueryParams) => {
      const { depVar = DEFAULT_DEP_VAR_METRIC, depVarScaler = [] } = params as {
        depVar?: string
        depVarScaler?: string[]
      }
      return {
        measures: [
          "company_platform_brand_value.engagements",
          "company_platform_brand_value.revenue30Days",
          "company_platform_brand_value.revenue180Days",
          // "company_platform_brand_value.revenuePerEngagement30Days",
          // "company_platform_brand_value.revenuePerEngagement180Days",
          "company_platform_brand_value.channels_count",
        ],
        timeDimensions: [
          {
            dimension: "company_platform_brand_value.engagement_date",
          },
        ],
        filters: [
          {
            and: [
              {
                dimension: "company_platform_brand_value.dep_var",
                operator: "equals",
                values: [depVar],
              },
              depVarScaler.length > 0
                ? {
                    dimension: "company_platform_brand_value.dep_var_scaler",
                    operator: "equals",
                    values: depVarScaler,
                  }
                : undefined,
            ].filter(f => !!f) as Filter[],
          },
        ],
      }
    },
    postProcess: (data: MetricDataItem[]) => {
      const requiredFields = [
        // "company_platform_brand_value.engagements",
        "company_platform_brand_value.revenue30Days",
        "company_platform_brand_value.revenue180Days",
      ]

      return [
        data.map((metric: MetricDataItem) => {
          const res = {
            ...metric,
          }
          requiredFields.forEach((field: string) => {
            res[`${field}_count`] =
              metric["company_platform_brand_value.channels_count"]
          })
          return res
        }),
        requiredFields,
      ]
    },
  },
  brand_value_conversions_by_platform_table: {
    q: (params: QueryParams) => {
      const { depVar = DEFAULT_DEP_VAR_METRIC, depVarScaler = [] } = params as {
        depVar?: string
        depVarScaler?: string[]
      }
      return {
        measures: [
          "company_platform_brand_value.engagements",
          "company_platform_brand_value.revenue30Days",
          "company_platform_brand_value.revenue180Days",
          // "company_platform_brand_value.revenuePerEngagement30Days",
          // "company_platform_brand_value.revenuePerEngagement180Days",
        ],
        dimensions: ["company_platform_brand_value.platform"],
        filters: [
          {
            and: [
              {
                dimension: "company_platform_brand_value.dep_var",
                operator: "equals",
                values: [depVar],
              },
              depVarScaler.length > 0
                ? {
                    dimension: "company_platform_brand_value.dep_var_scaler",
                    operator: "equals",
                    values: depVarScaler,
                  }
                : undefined,
            ].filter(f => !!f) as Filter[],
          },
        ],
        timeDimensions: [
          {
            dimension: "company_platform_brand_value.engagement_date",
          },
        ],
      }
    },
  },
  brand_value_conversions_by_platform_table_summary: {
    q: (params: QueryParams) => {
      const { depVar = DEFAULT_DEP_VAR_METRIC, depVarScaler = [] } = params as {
        depVar?: string
        depVarScaler?: string[]
      }
      return {
        measures: [
          "company_platform_brand_value.engagements",
          "company_platform_brand_value.revenue30Days",
          "company_platform_brand_value.revenue180Days",
          // "company_platform_brand_value.revenuePerEngagement30Days",
          // "company_platform_brand_value.revenuePerEngagement180Days",
          "company_platform_brand_value.platforms_count",
        ],
        timeDimensions: [
          {
            dimension: "company_platform_brand_value.engagement_date",
          },
        ],
        filters: [
          {
            and: [
              {
                dimension: "company_platform_brand_value.dep_var",
                operator: "equals",
                values: [depVar],
              },
              depVarScaler.length > 0
                ? {
                    dimension: "company_platform_brand_value.dep_var_scaler",
                    operator: "equals",
                    values: depVarScaler,
                  }
                : undefined,
            ].filter(f => !!f) as Filter[],
          },
        ],
      }
    },
    postProcess: (data: MetricDataItem[]) => {
      const requiredFields = [
        // "company_platform_brand_value.engagements",
        "company_platform_brand_value.revenue30Days",
        "company_platform_brand_value.revenue180Days",
      ]

      return [
        data.map((metric: MetricDataItem) => {
          const res = {
            ...metric,
          }
          requiredFields.forEach((field: string) => {
            res[`${field}_count`] =
              metric["company_platform_brand_value.platforms_count"]
          })
          return res
        }),
        requiredFields,
      ]
    },
  },
  brand_value_by_channel_group_chart: {
    q: (params: QueryParams) => {
      const { depVar = DEFAULT_DEP_VAR_METRIC, depVarScaler = [] } = params as {
        depVar?: string
        depVarScaler?: string[]
      }
      return {
        measures: [
          "company_platform_brand_value.ad_channel_group_brand_value",
          "company_platform_brand_value.organic_channel_group_brand_value",
          "company_platform_brand_value.mentions_channel_group_brand_value",
          "company_platform_brand_value.subscription_channel_group_brand_value",
          "company_platform_brand_value.unattributed_channel_group_brand_value",
          // "company_platform_brand_value.search_channel_group_brand_value",
        ],
        timeDimensions: [
          {
            dimension: "company_platform_brand_value.engagement_date",
          },
        ],
        filters: [
          {
            and: [
              {
                dimension: "company_platform_brand_value.dep_var",
                operator: "equals",
                values: [depVar],
              },
              depVarScaler.length > 0
                ? {
                    dimension: "company_platform_brand_value.dep_var_scaler",
                    operator: "equals",
                    values: depVarScaler,
                  }
                : undefined,
            ].filter(f => !!f) as Filter[],
          },
        ],
      }
    },
    postProcess(data: MetricDataItem[]) {
      const dataKeys = [
        "company_platform_brand_value.ad_channel_group_brand_value",
        "company_platform_brand_value.organic_channel_group_brand_value",
        "company_platform_brand_value.mentions_channel_group_brand_value",
        "company_platform_brand_value.subscription_channel_group_brand_value",
        "company_platform_brand_value.unattributed_channel_group_brand_value",
        // "company_platform_brand_value.search_channel_group_brand_value",
      ]
      return [
        data.map((item: MetricDataItem) => {
          const total = dataKeys.reduce(
            (acc, key) => acc + +(item[key] as number),
            0,
          )
          const newItem: ProcessedMetricItem = {
            ...item,
          }
          dataKeys.forEach(key => {
            newItem[`${key}_coef`] = (item[key] as number) / total
          })
          return newItem
        }),
      ]
    },
  },
  brand_value_by_payout_curve_chart: {
    q: () => ({
      measures: [
        "metrics.d30_brand_value",
        "metrics.d60_brand_value",
        "metrics.d90_brand_value",
        "metrics.d120_brand_value",
        "metrics.d150_brand_value",
        "metrics.d180_brand_value",
      ],
      timeDimensions: [
        {
          dimension: "metrics.date",
        },
      ],
    }),
    postProcess(data: MetricDataItem[]) {
      const dataKeys = [
        "metrics.d30_brand_value",
        "metrics.d60_brand_value",
        "metrics.d90_brand_value",
        "metrics.d120_brand_value",
        "metrics.d150_brand_value",
        "metrics.d180_brand_value",
      ]
      const totalKey = "metrics.d180_brand_value"
      return [
        data.map((item: MetricDataItem) => {
          const newItem: ProcessedMetricItem = {
            ...item,
          }
          dataKeys.forEach(key => {
            newItem[`${key}_coef`] =
              (item[key] as number) / (item[totalKey] as number)
          })
          return newItem
        }),
      ]
    },
  },
  brand_value_stacked_chart: {
    q: (params: QueryParams) => {
      const { depVarScaler = [] } = params as { depVarScaler?: string[] }
      return {
        measures: [
          "metrics.ga_baseline_revenue",
          "metrics.ga_paid_revenue",
          "metrics.ga_triggered_revenue",
          "metrics.ga_revenue_baseline_plus_paid_brand_search",
          "metrics.ga_amazon_baseline_revenue_plus_paid_brand_search",
          "metrics.ga_amazon_baseline_revenue",
          "metrics.ga_total_revenue_from_all_sessions",
          "metrics.ga_multi_property_baseline_revenue",
          "metrics.ga_multi_property_paid_revenue",
          "metrics.ga_multi_property_triggered_revenue",
          "metrics.ga_multi_property_total_revenue_from_all_sessions",
          "metrics.ga_multi_property_amazon_baseline_revenue_plus_paid_brand_search",
          "metrics.ga_multi_property_revenue_baseline_plus_paid_brand_search",
          "metrics.ga_multi_property_revenue_plus_amazon",
          "metrics.shopify_baseline_revenue",
          "metrics.shopify_paid_revenue",
          "metrics.shopify_triggered_revenue",
          "metrics.shopify_baseline_plus_paid_brand_search_revenue",
          "metrics.shopify_baseline_plus_subscription_plus_paid_brand_search_revenue",
          "metrics.shopify_amazon_baseline_revenue",
          "metrics.shopify_plus_subscription_amazon_baseline_revenue",
          "metrics.shopify_total_traffic_source_revenue",
          "metrics.shopify_subscription_revenue",

          "metrics.shopify_total_customer_revenue",
          "metrics.shopify_total_new_customer_revenue",
          "metrics.shopify_total_repeat_customer_revenue",

          "metrics.shopify_ecomm_new_customer_revenue",
          "metrics.shopify_ecomm_repeat_customer_revenue",
          "metrics.shopify_ecomm_total_customer_revenue",

          "metrics.shopify_dtc_retail_new_customer_revenue",
          "metrics.shopify_dtc_retail_repeat_customer_revenue",
          "metrics.shopify_dtc_retail_total_customer_revenue",

          "metrics.shopify_third_party_ecomm_new_customer_revenue",
          "metrics.shopify_third_party_ecomm_repeat_customer_revenue",
          "metrics.shopify_third_party_ecomm_total_customer_revenue",

          "metrics.shopify_subscription_new_customer_revenue",
          "metrics.shopify_subscription_repeat_customer_revenue",
          "metrics.shopify_subscription_total_customer_revenue",

          "metrics.amazon_owned_revenue",
          "metrics.amazon_ads_total_spend",
          "metrics.amazon_total_revenue",

          "metrics.total_social_ad_spend",
          "metrics.total_ad_spend",
          "metrics.total_brand_roas",

          "metrics.d30_brand_value",
          "metrics.d60_brand_value",
          "metrics.d90_brand_value",
          "metrics.d120_brand_value",
          "metrics.d150_brand_value",
          "metrics.d180_brand_value",

          "metrics.google_search_organic_branded_search_impressions",
          "metrics.google_ads_paid_branded_search_impressions",
          "metrics.google_search_organic_branded_search_clicks",
          "metrics.google_ads_paid_branded_search_clicks",
          "metrics.total_google_search_clicks",

          "metrics.brand_factor",
          "metrics.monetization_factor",

          "metrics.tiktok_videos_shares",
          "metrics.tiktok_ads_shares",
          "metrics.instagram_business_shares",
          "metrics.instagram_ads_shares",
          "metrics.facebook_pages_shares",
          "metrics.facebook_ads_shares",
          "metrics.youtube_channel_shares",

          "metrics.instagram_business_saves",
          "metrics.instagram_ads_saves",
          "metrics.tiktok_ads_saves",
          "metrics.youtube_channel_saves",

          "metrics.klaviyo_email_subscribers",
          "metrics.total_new_subscribers",

          "metrics.tiktok_ads_follows",
          "metrics.facebook_pages_follows",
          "metrics.youtube_channel_follows",
          "metrics.total_new_followers_without_tiktok",
          "metrics.total_new_followers",
          "metrics.facebook_ads_follows",

          "metrics.instagram_business_likes",
          "metrics.instagram_ads_likes",
          "metrics.tiktok_videos_likes",
          "metrics.tiktok_ads_likes",
          "metrics.facebook_pages_likes",
          "metrics.facebook_ads_likes",
          "metrics.youtube_channel_likes",
          "metrics.total_likes",

          "metrics.instagram_business_comments",
          "metrics.instagram_ads_comments",
          "metrics.tiktok_videos_comments",
          "metrics.tiktok_ads_comments",
          "metrics.facebook_pages_comments",
          "metrics.facebook_ads_comments",
          "metrics.youtube_channel_comments",
          "metrics.total_comments",

          // Channel Total Metrics
          "metrics.facebook_total_likes",
          "metrics.facebook_total_comments",
          "metrics.facebook_total_shares",
          "metrics.facebook_total_saves",
          "metrics.facebook_total_follows",
          "metrics.instagram_total_likes",
          "metrics.instagram_total_comments",
          "metrics.instagram_total_shares",
          "metrics.instagram_total_saves",
          "metrics.instagram_total_follows",
          "metrics.tiktok_total_likes",
          "metrics.tiktok_total_comments",
          "metrics.tiktok_total_shares",
          "metrics.tiktok_total_follows",

          // Aggregate Totals
          "metrics.total_shares",
          "metrics.total_saves",
          "metrics.total_platform_engagements",
          "metrics.total_followers_plus_subscribers",
          "metrics.total_digital_behavior",

          // Traffic Metrics
          "metrics.ga_baseline_sessions",
          "metrics.ga_triggered_sessions",
          "metrics.ga_paid_sessions",
          "metrics.ga_sessions",
        ],
        filters: [
          {
            and: [
              depVarScaler.length > 0
                ? {
                    dimension: "metrics.dep_var_scaler",
                    operator: "equals",
                    values: depVarScaler,
                  }
                : undefined,
            ].filter(f => !!f) as Filter[],
          },
        ],
        order: {
          "metrics.date": "asc",
        },
        timeDimensions: [
          {
            dimension: "metrics.date",
          },
        ],
      }
    },
    postProcess: (
      data: MetricDataItem[],
      {
        granularity = AggregationType.DAILY,
      }: {
        granularity?: AggregationType
      },
    ) => {
      const gran = GranularityMapping[granularity]
      // Weekly keys may come back rewritten to the company's custom week
      // granularity (e.g. `.week_sunday`); resolve both cubes' keys from the
      // rows so the two sources keep merging into the same buckets.
      const modeledDateKey = resolveTimeDimensionKey(
        data,
        "company_platform_brand_value.engagement_date",
        `company_platform_brand_value.engagement_date.${gran}`,
      )
      const dailyDateKey = resolveTimeDimensionKey(
        data,
        "metrics.date",
        `metrics.date.${gran}`,
      )

      const newData: ProcessedMetricItem = {}
      for (const dataPoint of data) {
        const currentDate = (dataPoint[dailyDateKey] ||
          dataPoint[modeledDateKey]) as string

        if (!newData[currentDate]) {
          newData[currentDate] = { date: currentDate }
        }

        newData[currentDate] = {
          ...(newData[currentDate] as Record<string, unknown>),
          ...dataPoint,
        }
      }

      return [Object.values(newData) as ProcessedMetricItem[]]
    },
  },
  brand_media_optimization_campaign_table: {
    enrichTags: { itemType: "campaign", idKey: "campaign_id" },
    q: {
      measures: [
        `company_media_metrics_campaigns.budget_agg`,
        `company_media_metrics_campaigns.lower_brand_value_agg`,
        `company_media_metrics_campaigns.upper_brand_value_agg`,
        `company_media_metrics_campaigns.revenue_from_search`,
        `company_media_metrics_campaigns.revenue_from_search_roas`,
        `company_media_metrics_campaigns.spend`,
        `company_media_metrics_campaigns.shares`,
        `company_media_metrics_campaigns.likes`,
        `company_media_metrics_campaigns.comments`,
        `company_media_metrics_campaigns.follows`,
        `company_media_metrics_campaigns.saves`,
        `company_media_metrics_campaigns.d30_brand_value`,
        `company_media_metrics_campaigns.d60_brand_value`,
        `company_media_metrics_campaigns.d90_brand_value`,
        `company_media_metrics_campaigns.purchases`,
        `company_media_metrics_campaigns.brand_value_agg`,
        `company_media_metrics_campaigns.facebook_ads_brand_value`,
        `company_media_metrics_campaigns.facebook_ads_spend`,
        `company_media_metrics_campaigns.facebook_ads_comments`,
        `company_media_metrics_campaigns.facebook_ads_shares`,
        `company_media_metrics_campaigns.facebook_ads_new_fans`,
        `company_media_metrics_campaigns.facebook_ads_reactions`,
        `company_media_metrics_campaigns.facebook_ads_purchases`,
        `company_media_metrics_campaigns.facebook_ads_saves`,
        `company_media_metrics_campaigns.instagram_ads_brand_value`,
        `company_media_metrics_campaigns.instagram_ads_spend`,
        `company_media_metrics_campaigns.instagram_ads_follows`,
        `company_media_metrics_campaigns.instagram_ads_comments`,
        `company_media_metrics_campaigns.instagram_ads_likes`,
        `company_media_metrics_campaigns.instagram_ads_shares`,
        `company_media_metrics_campaigns.instagram_ads_purchases`,
        `company_media_metrics_campaigns.instagram_ads_saves`,
        `company_media_metrics_campaigns.tiktok_ads_spend`,
        `company_media_metrics_campaigns.tiktok_ads_follows`,
        `company_media_metrics_campaigns.tiktok_ads_comments`,
        `company_media_metrics_campaigns.tiktok_ads_shares`,
        `company_media_metrics_campaigns.tiktok_ads_likes`,
        `company_media_metrics_campaigns.tiktok_ads_purchases`,
        `company_media_metrics_campaigns.pinterest_ads_spend`,
        `company_media_metrics_campaigns.pinterest_ads_saves`,
        `company_media_metrics_campaigns.pinterest_ads_purchases`,
        `company_media_metrics_campaigns.long_term_revenue`,
        `company_media_metrics_campaigns.short_term_revenue_roas`,
        `company_media_metrics_campaigns.brand_value_over_spend`,
        `company_media_metrics_campaigns.long_term_revenue_roas`,
        `company_media_metrics_campaigns.modeled_revenue`,
        `company_media_metrics_campaigns.modeled_revenue_over_spend`,
        `company_media_metrics_campaigns.estimated_d180_brand_value`,
        `company_media_metrics_campaigns.modeled_brand_value_over_spend`,
      ],
      dimensions: [
        `company_media_metrics_campaigns.media_channel`,
        `company_media_metrics_campaigns.campaign_name`,
        `company_media_metrics_campaigns.campaign_status`,
        `company_media_metrics_campaigns.campaign_id`,
      ],
      timeDimensions: [
        {
          dimension: "company_media_metrics_campaigns.md_date",
        },
      ],
    },
    postProcess: (data: MetricDataItem[]) => {
      const prefix = "company_media_metrics_campaigns"
      const rows = data.map(item => {
        const itemWithSimpleKeys: ProcessedMetricItem = {}

        Object.keys(item).forEach(key => {
          const shortKey = key.replace(`${prefix}.`, "")
          itemWithSimpleKeys[shortKey] = item[key]
        })
        return {
          ...itemWithSimpleKeys,
          media_channel:
            MEDIA_CHANNEL_TO_SOURCE_ID_MAPPING[
              itemWithSimpleKeys.media_channel as string
            ] ?? itemWithSimpleKeys.media_channel,
        }
      })
      // Tags + asset resolution are intentionally NOT done here. They were
      // blocking first paint by 2-5s. Tags are now subscribed live in
      // TableVisualization; assets are resolved fire-and-forget in connect.ts
      // after the cube cache is populated.
      return [rows]
    },
  },
  brand_media_optimization_campaign_table_summary: {
    q: {
      measures: [
        `company_media_metrics_campaigns.budget_agg`,
        `company_media_metrics_campaigns.budget_agg_count`,
        `company_media_metrics_campaigns.lower_brand_value_agg`,
        `company_media_metrics_campaigns.upper_brand_value_agg`,
        `company_media_metrics_campaigns.revenue_from_search`,
        `company_media_metrics_campaigns.revenue_from_search_roas`,
        `company_media_metrics_campaigns.spend`,
        `company_media_metrics_campaigns.spend_count`,
        `company_media_metrics_campaigns.shares`,
        `company_media_metrics_campaigns.shares_count`,
        `company_media_metrics_campaigns.likes`,
        `company_media_metrics_campaigns.likes_count`,
        `company_media_metrics_campaigns.comments`,
        `company_media_metrics_campaigns.comments_count`,
        `company_media_metrics_campaigns.follows`,
        `company_media_metrics_campaigns.follows_count`,
        `company_media_metrics_campaigns.saves`,
        `company_media_metrics_campaigns.saves_count`,
        `company_media_metrics_campaigns.d30_brand_value`,
        `company_media_metrics_campaigns.d60_brand_value`,
        `company_media_metrics_campaigns.d90_brand_value`,
        `company_media_metrics_campaigns.purchases`,
        `company_media_metrics_campaigns.purchases_count`,
        `company_media_metrics_campaigns.brand_value_agg`,
        `company_media_metrics_campaigns.brand_value_agg_count`,
        `company_media_metrics_campaigns.modeled_revenue`,
        `company_media_metrics_campaigns.modeled_revenue_over_spend`,
        `company_media_metrics_campaigns.estimated_d180_brand_value`,
        `company_media_metrics_campaigns.modeled_brand_value_over_spend`,
        `company_media_metrics_campaigns.brand_value_over_spend`,
        `company_media_metrics_campaigns.short_term_revenue_roas`,
        `company_media_metrics_campaigns.long_term_revenue_roas`,
        `company_media_metrics_campaigns.facebook_ads_brand_value`,
        `company_media_metrics_campaigns.facebook_ads_brand_value_count`,
        `company_media_metrics_campaigns.facebook_ads_spend`,
        `company_media_metrics_campaigns.facebook_ads_spend_count`,
        `company_media_metrics_campaigns.facebook_ads_comments`,
        `company_media_metrics_campaigns.facebook_ads_comments_count`,
        `company_media_metrics_campaigns.facebook_ads_shares`,
        `company_media_metrics_campaigns.facebook_ads_shares_count`,
        `company_media_metrics_campaigns.facebook_ads_new_fans`,
        `company_media_metrics_campaigns.facebook_ads_new_fans_count`,
        `company_media_metrics_campaigns.facebook_ads_reactions`,
        `company_media_metrics_campaigns.facebook_ads_reactions_count`,
        `company_media_metrics_campaigns.facebook_ads_saves`,
        `company_media_metrics_campaigns.facebook_ads_saves_count`,
        `company_media_metrics_campaigns.facebook_ads_purchases`,
        `company_media_metrics_campaigns.facebook_ads_purchases_count`,
        `company_media_metrics_campaigns.instagram_ads_brand_value`,
        `company_media_metrics_campaigns.instagram_ads_brand_value_count`,
        `company_media_metrics_campaigns.instagram_ads_spend`,
        `company_media_metrics_campaigns.instagram_ads_spend_count`,
        `company_media_metrics_campaigns.instagram_ads_follows`,
        `company_media_metrics_campaigns.instagram_ads_follows_count`,
        `company_media_metrics_campaigns.instagram_ads_comments`,
        `company_media_metrics_campaigns.instagram_ads_comments_count`,
        `company_media_metrics_campaigns.instagram_ads_likes`,
        `company_media_metrics_campaigns.instagram_ads_likes_count`,
        `company_media_metrics_campaigns.instagram_ads_shares`,
        `company_media_metrics_campaigns.instagram_ads_shares_count`,
        `company_media_metrics_campaigns.instagram_ads_purchases`,
        `company_media_metrics_campaigns.instagram_ads_purchases_count`,
        `company_media_metrics_campaigns.instagram_ads_saves`,
        `company_media_metrics_campaigns.instagram_ads_saves_count`,
        `company_media_metrics_campaigns.tiktok_ads_spend`,
        `company_media_metrics_campaigns.tiktok_ads_spend_count`,
        `company_media_metrics_campaigns.tiktok_ads_follows`,
        `company_media_metrics_campaigns.tiktok_ads_follows_count`,
        `company_media_metrics_campaigns.tiktok_ads_comments`,
        `company_media_metrics_campaigns.tiktok_ads_comments_count`,
        `company_media_metrics_campaigns.tiktok_ads_shares`,
        `company_media_metrics_campaigns.tiktok_ads_shares_count`,
        `company_media_metrics_campaigns.tiktok_ads_likes`,
        `company_media_metrics_campaigns.tiktok_ads_likes_count`,
        `company_media_metrics_campaigns.tiktok_ads_purchases`,
        `company_media_metrics_campaigns.pinterest_ads_spend`,
        `company_media_metrics_campaigns.pinterest_ads_saves`,
        `company_media_metrics_campaigns.pinterest_ads_purchases`,
        `company_media_metrics_campaigns.tiktok_ads_purchases_count`,
        `company_media_metrics_campaigns.pinterest_ads_spend_count`,
        `company_media_metrics_campaigns.pinterest_ads_saves_count`,
        `company_media_metrics_campaigns.pinterest_ads_purchases_count`,
        `company_media_metrics_campaigns.long_term_revenue`,
        `company_media_metrics_campaigns.long_term_revenue_count`,
      ],
      timeDimensions: [
        {
          dimension: "company_media_metrics_campaigns.md_date",
        },
      ],
    },
    postProcess: (data: MetricDataItem[]) => {
      const prefix = "company_media_metrics_campaigns"
      return [
        data.map(item => {
          const itemWithSimpleKeys: ProcessedMetricItem = {}

          Object.keys(item).forEach(key => {
            const shortKey = key.replace(`${prefix}.`, "")
            itemWithSimpleKeys[shortKey] = item[key]
          })

          return {
            ...itemWithSimpleKeys,
            brand_value: itemWithSimpleKeys.brand_value_agg,
          }
        }),
        [
          `budget_agg`,
          `lower_brand_value_agg`,
          `upper_brand_value_agg`,
          `revenue_from_search`,
          `revenue_from_search_roas`,
          `spend`,
          `shares`,
          `likes`,
          `comments`,
          `follows`,
          `saves`,
          `purchases`,
          `brand_value_agg`,
          `facebook_ads_brand_value`,
          `facebook_ads_spend`,
          `facebook_ads_comments`,
          `facebook_ads_shares`,
          `facebook_ads_new_fans`,
          `facebook_ads_reactions`,
          `facebook_ads_saves`,
          `facebook_ads_purchases`,
          `instagram_ads_brand_value`,
          `instagram_ads_spend`,
          `instagram_ads_follows`,
          `instagram_ads_comments`,
          `instagram_ads_likes`,
          `instagram_ads_shares`,
          `instagram_ads_purchases`,
          `instagram_ads_saves`,
          `tiktok_ads_spend`,
          `tiktok_ads_follows`,
          `tiktok_ads_comments`,
          `tiktok_ads_shares`,
          `tiktok_ads_likes`,
          `tiktok_ads_purchases`,
          `pinterest_ads_spend`,
          `pinterest_ads_saves`,
          `pinterest_ads_purchases`,
          `long_term_revenue`,
        ],
      ]
    },
  },
  brand_media_optimization_adsets_table: {
    enrichTags: { itemType: "adset", idKey: "adset_id" },
    q: {
      measures: [
        `company_media_metrics_adsets.budget_agg`,
        `company_media_metrics_adsets.lower_brand_value_agg`,
        `company_media_metrics_adsets.upper_brand_value_agg`,
        `company_media_metrics_adsets.revenue_from_search`,
        `company_media_metrics_adsets.revenue_from_search_roas`,
        `company_media_metrics_adsets.spend`,
        `company_media_metrics_adsets.shares`,
        `company_media_metrics_adsets.likes`,
        `company_media_metrics_adsets.comments`,
        `company_media_metrics_adsets.follows`,
        `company_media_metrics_adsets.saves`,
        `company_media_metrics_adsets.purchases`,
        `company_media_metrics_adsets.brand_value_agg`,
        `company_media_metrics_adsets.d30_brand_value`,
        `company_media_metrics_adsets.d60_brand_value`,
        `company_media_metrics_adsets.d90_brand_value`,
        `company_media_metrics_adsets.facebook_ads_brand_value`,
        `company_media_metrics_adsets.facebook_ads_spend`,
        `company_media_metrics_adsets.facebook_ads_comments`,
        `company_media_metrics_adsets.facebook_ads_shares`,
        `company_media_metrics_adsets.facebook_ads_new_fans`,
        `company_media_metrics_adsets.facebook_ads_reactions`,
        `company_media_metrics_adsets.facebook_ads_purchases`,
        `company_media_metrics_adsets.facebook_ads_saves`,
        `company_media_metrics_adsets.instagram_ads_brand_value`,
        `company_media_metrics_adsets.instagram_ads_spend`,
        `company_media_metrics_adsets.instagram_ads_follows`,
        `company_media_metrics_adsets.instagram_ads_comments`,
        `company_media_metrics_adsets.instagram_ads_likes`,
        `company_media_metrics_adsets.instagram_ads_shares`,
        `company_media_metrics_adsets.instagram_ads_purchases`,
        `company_media_metrics_adsets.instagram_ads_saves`,
        `company_media_metrics_adsets.tiktok_ads_spend`,
        `company_media_metrics_adsets.tiktok_ads_follows`,
        `company_media_metrics_adsets.tiktok_ads_comments`,
        `company_media_metrics_adsets.tiktok_ads_shares`,
        `company_media_metrics_adsets.tiktok_ads_likes`,
        `company_media_metrics_adsets.tiktok_ads_purchases`,
        `company_media_metrics_adsets.pinterest_ads_spend`,
        `company_media_metrics_adsets.pinterest_ads_saves`,
        `company_media_metrics_adsets.pinterest_ads_purchases`,
        `company_media_metrics_adsets.long_term_revenue`,
        `company_media_metrics_adsets.short_term_revenue_roas`,
        `company_media_metrics_adsets.brand_value_over_spend`,
        `company_media_metrics_adsets.long_term_revenue_roas`,
        `company_media_metrics_adsets.modeled_revenue`,
        `company_media_metrics_adsets.modeled_revenue_over_spend`,
        `company_media_metrics_adsets.estimated_d180_brand_value`,
        `company_media_metrics_adsets.modeled_brand_value_over_spend`,
      ],
      dimensions: [
        `company_media_metrics_adsets.media_channel`,
        `company_media_metrics_adsets.campaign_name`,
        `company_media_metrics_adsets.campaign_status`,
        `company_media_metrics_adsets.adset_name`,
        `company_media_metrics_adsets.adset_status`,
        `company_media_metrics_adsets.adset_id`,
        `company_media_metrics_adsets.campaign_id`,
        `company_media_metrics_adsets.initiative_type`,
      ],
      timeDimensions: [
        {
          dimension: "company_media_metrics_adsets.md_date",
        },
      ],
    },
    postProcess: (data: MetricDataItem[]) => {
      const prefix = `company_media_metrics_adsets`
      const rows = data.map(item => {
        const itemWithSimpleKeys: ProcessedMetricItem = {}

        Object.keys(item).forEach(key => {
          const shortKey = key.replace(`${prefix}.`, "")
          itemWithSimpleKeys[shortKey] = item[key]
        })
        return {
          ...itemWithSimpleKeys,
          media_channel:
            MEDIA_CHANNEL_TO_SOURCE_ID_MAPPING[
              itemWithSimpleKeys.media_channel as string
            ] ?? itemWithSimpleKeys.media_channel,
        }
      })
      return [rows]
    },
  },
  custom_attribution_brand_media_optimization_adsets_table: {
    enrichTags: { itemType: "adset", idKey: "adset_id" },
    q: {
      measures: [
        `company_media_metrics_adsets.budget_agg`,
        `company_media_metrics_adsets.lower_brand_value_agg`,
        `company_media_metrics_adsets.upper_brand_value_agg`,
        `company_media_metrics_adsets.revenue_from_search`,
        `company_media_metrics_adsets.revenue_from_search_roas`,
        `company_media_metrics_adsets.modeled_revenue`,
        `company_media_metrics_adsets.meta_purchase_value_7d_click_1d_view`,
        `company_media_metrics_adsets.spend`,
        `company_media_metrics_adsets.shares`,
        `company_media_metrics_adsets.likes`,
        `company_media_metrics_adsets.comments`,
        `company_media_metrics_adsets.follows`,
        `company_media_metrics_adsets.saves`,
        `company_media_metrics_adsets.purchases`,
        `company_media_metrics_adsets.brand_value_agg`,
        `company_media_metrics_adsets.d30_brand_value`,
        `company_media_metrics_adsets.d60_brand_value`,
        `company_media_metrics_adsets.d90_brand_value`,
        `company_media_metrics_adsets.facebook_ads_brand_value`,

        `company_media_metrics_adsets.facebook_ads_spend`,
        `company_media_metrics_adsets.facebook_ads_comments`,
        `company_media_metrics_adsets.facebook_ads_shares`,
        `company_media_metrics_adsets.facebook_ads_new_fans`,
        `company_media_metrics_adsets.facebook_ads_reactions`,
        `company_media_metrics_adsets.facebook_ads_purchases`,
        `company_media_metrics_adsets.facebook_ads_saves`,
        `company_media_metrics_adsets.instagram_ads_brand_value`,
        `company_media_metrics_adsets.instagram_ads_spend`,
        `company_media_metrics_adsets.instagram_ads_follows`,
        `company_media_metrics_adsets.instagram_ads_comments`,
        `company_media_metrics_adsets.instagram_ads_likes`,
        `company_media_metrics_adsets.instagram_ads_shares`,
        `company_media_metrics_adsets.instagram_ads_purchases`,
        `company_media_metrics_adsets.instagram_ads_saves`,
        `company_media_metrics_adsets.tiktok_ads_spend`,
        `company_media_metrics_adsets.tiktok_ads_follows`,
        `company_media_metrics_adsets.tiktok_ads_comments`,
        `company_media_metrics_adsets.tiktok_ads_shares`,
        `company_media_metrics_adsets.tiktok_ads_likes`,
        `company_media_metrics_adsets.tiktok_ads_purchases`,
        `company_media_metrics_adsets.pinterest_ads_spend`,
        `company_media_metrics_adsets.pinterest_ads_saves`,
        `company_media_metrics_adsets.pinterest_ads_purchases`,
        `company_media_metrics_adsets.long_term_revenue`,
        `company_media_metrics_adsets.short_term_revenue_roas`,
        `company_media_metrics_adsets.brand_value_over_spend`,
        `company_media_metrics_adsets.long_term_revenue_roas`,
        `company_media_metrics_adsets.purchase_value_1d_view`,
        `company_media_metrics_adsets.purchase_value_1d_engaged_view`,
        `company_media_metrics_adsets.ga4_total_revenue`,

        // Brand Values - Normal
        `company_media_metrics_adsets.d30_brand_value`,
        `company_media_metrics_adsets.d60_brand_value`,
        `company_media_metrics_adsets.d90_brand_value`,
        `company_media_metrics_adsets.d120_brand_value`,
        `company_media_metrics_adsets.d150_brand_value`,

        // Brand Values - Estimated
        `company_media_metrics_adsets.estimated_d30_brand_value`,
        `company_media_metrics_adsets.estimated_d60_brand_value`,
        `company_media_metrics_adsets.estimated_d90_brand_value`,
        `company_media_metrics_adsets.estimated_d120_brand_value`,
        `company_media_metrics_adsets.estimated_d150_brand_value`,
        `company_media_metrics_adsets.estimated_d180_brand_value`,
      ],
      dimensions: [
        `company_media_metrics_adsets.media_channel`,
        `company_media_metrics_adsets.campaign_name`,
        `company_media_metrics_adsets.campaign_status`,
        `company_media_metrics_adsets.adset_name`,
        `company_media_metrics_adsets.adset_status`,
        `company_media_metrics_adsets.adset_id`,
        `company_media_metrics_adsets.campaign_id`,
        `company_media_metrics_adsets.objective`,
        `company_media_metrics_adsets.optimization_goal`,
        `company_media_metrics_adsets.initiative_type`,
      ],
      timeDimensions: [
        {
          dimension: "company_media_metrics_adsets.md_date",
        },
      ],
    },
    postProcess: (data: MetricDataItem[]) => {
      const prefix = `company_media_metrics_adsets`
      const rows = data.map(item => {
        const itemWithSimpleKeys: ProcessedMetricItem = {}

        Object.keys(item).forEach(key => {
          const shortKey = key.replace(`${prefix}.`, "")
          itemWithSimpleKeys[shortKey] = item[key]
        })
        return {
          ...itemWithSimpleKeys,
          media_channel:
            MEDIA_CHANNEL_TO_SOURCE_ID_MAPPING[
              itemWithSimpleKeys.media_channel as string
            ] ?? itemWithSimpleKeys.media_channel,
        }
      })
      return [rows]
    },
  },
  brand_media_optimization_adsets_table_summary: {
    q: {
      measures: [
        `company_media_metrics_adsets.budget_agg`,
        `company_media_metrics_adsets.budget_agg_count`,
        `company_media_metrics_adsets.lower_brand_value_agg`,
        `company_media_metrics_adsets.upper_brand_value_agg`,
        `company_media_metrics_adsets.revenue_from_search`,
        `company_media_metrics_adsets.revenue_from_search_roas`,
        `company_media_metrics_adsets.spend`,
        `company_media_metrics_adsets.spend_count`,
        `company_media_metrics_adsets.shares`,
        `company_media_metrics_adsets.shares_count`,
        `company_media_metrics_adsets.likes`,
        `company_media_metrics_adsets.likes_count`,
        `company_media_metrics_adsets.comments`,
        `company_media_metrics_adsets.comments_count`,
        `company_media_metrics_adsets.follows`,
        `company_media_metrics_adsets.follows_count`,
        `company_media_metrics_adsets.saves`,
        `company_media_metrics_adsets.saves_count`,
        `company_media_metrics_adsets.purchases`,
        `company_media_metrics_adsets.purchases_count`,
        `company_media_metrics_adsets.brand_value_agg`,
        `company_media_metrics_adsets.brand_value_agg_count`,
        `company_media_metrics_adsets.modeled_revenue`,
        `company_media_metrics_adsets.modeled_revenue_over_spend`,
        `company_media_metrics_adsets.estimated_d180_brand_value`,
        `company_media_metrics_adsets.modeled_brand_value_over_spend`,
        `company_media_metrics_adsets.brand_value_over_spend`,
        `company_media_metrics_adsets.short_term_revenue_roas`,
        `company_media_metrics_adsets.long_term_revenue_roas`,
        `company_media_metrics_adsets.d30_brand_value`,
        `company_media_metrics_adsets.d60_brand_value`,
        `company_media_metrics_adsets.d90_brand_value`,
        `company_media_metrics_adsets.facebook_ads_brand_value`,
        `company_media_metrics_adsets.facebook_ads_brand_value_count`,
        `company_media_metrics_adsets.facebook_ads_spend`,
        `company_media_metrics_adsets.facebook_ads_spend_count`,
        `company_media_metrics_adsets.facebook_ads_comments`,
        `company_media_metrics_adsets.facebook_ads_comments_count`,
        `company_media_metrics_adsets.facebook_ads_shares`,
        `company_media_metrics_adsets.facebook_ads_shares_count`,
        `company_media_metrics_adsets.facebook_ads_new_fans`,
        `company_media_metrics_adsets.facebook_ads_new_fans_count`,
        `company_media_metrics_adsets.facebook_ads_reactions`,
        `company_media_metrics_adsets.facebook_ads_reactions_count`,
        `company_media_metrics_adsets.facebook_ads_saves`,
        `company_media_metrics_adsets.facebook_ads_saves_count`,
        `company_media_metrics_adsets.facebook_ads_purchases`,
        `company_media_metrics_adsets.facebook_ads_purchases_count`,
        `company_media_metrics_adsets.instagram_ads_brand_value`,
        `company_media_metrics_adsets.instagram_ads_brand_value_count`,
        `company_media_metrics_adsets.instagram_ads_spend`,
        `company_media_metrics_adsets.instagram_ads_spend_count`,
        `company_media_metrics_adsets.instagram_ads_follows`,
        `company_media_metrics_adsets.instagram_ads_follows_count`,
        `company_media_metrics_adsets.instagram_ads_comments`,
        `company_media_metrics_adsets.instagram_ads_comments_count`,
        `company_media_metrics_adsets.instagram_ads_likes`,
        `company_media_metrics_adsets.instagram_ads_likes_count`,
        `company_media_metrics_adsets.instagram_ads_shares`,
        `company_media_metrics_adsets.instagram_ads_shares_count`,
        `company_media_metrics_adsets.instagram_ads_purchases`,
        `company_media_metrics_adsets.instagram_ads_purchases_count`,
        `company_media_metrics_adsets.instagram_ads_saves`,
        `company_media_metrics_adsets.instagram_ads_saves_count`,
        `company_media_metrics_adsets.tiktok_ads_spend`,
        `company_media_metrics_adsets.tiktok_ads_spend_count`,
        `company_media_metrics_adsets.tiktok_ads_follows`,
        `company_media_metrics_adsets.tiktok_ads_follows_count`,
        `company_media_metrics_adsets.tiktok_ads_comments`,
        `company_media_metrics_adsets.tiktok_ads_comments_count`,
        `company_media_metrics_adsets.tiktok_ads_shares`,
        `company_media_metrics_adsets.tiktok_ads_shares_count`,
        `company_media_metrics_adsets.tiktok_ads_likes`,
        `company_media_metrics_adsets.tiktok_ads_likes_count`,
        `company_media_metrics_adsets.tiktok_ads_purchases`,
        `company_media_metrics_adsets.pinterest_ads_spend`,
        `company_media_metrics_adsets.pinterest_ads_saves`,
        `company_media_metrics_adsets.pinterest_ads_purchases`,
        `company_media_metrics_adsets.tiktok_ads_purchases_count`,
        `company_media_metrics_adsets.pinterest_ads_spend_count`,
        `company_media_metrics_adsets.pinterest_ads_saves_count`,
        `company_media_metrics_adsets.pinterest_ads_purchases_count`,
        `company_media_metrics_adsets.long_term_revenue`,
        `company_media_metrics_adsets.long_term_revenue_count`,
      ],
      timeDimensions: [
        {
          dimension: "company_media_metrics_adsets.md_date",
        },
      ],
    },
    postProcess: (data: MetricDataItem[]) => {
      const prefix = `company_media_metrics_adsets`
      return [
        data.map(item => {
          const itemWithSimpleKeys: ProcessedMetricItem = {}

          Object.keys(item).forEach(key => {
            const shortKey = key.replace(`${prefix}.`, "")
            itemWithSimpleKeys[shortKey] = item[key]
          })
          return {
            ...itemWithSimpleKeys,
            brand_value: itemWithSimpleKeys.brand_value_agg,
          }
        }),
        [
          `budget_agg`,
          `lower_brand_value_agg`,
          `upper_brand_value_agg`,
          `revenue_from_search`,
          `revenue_from_search_roas`,
          `spend`,
          `shares`,
          `likes`,
          `comments`,
          `follows`,
          `saves`,
          `purchases`,
          `brand_value_agg`,
          `d30_brand_value`,
          `d60_brand_value`,
          `d90_brand_value`,
          `facebook_ads_brand_value`,
          `facebook_ads_spend`,
          `facebook_ads_comments`,
          `facebook_ads_shares`,
          `facebook_ads_new_fans`,
          `facebook_ads_reactions`,
          `facebook_ads_saves`,
          `facebook_ads_purchases`,
          `instagram_ads_brand_value`,
          `instagram_ads_spend`,
          `instagram_ads_follows`,
          `instagram_ads_comments`,
          `instagram_ads_likes`,
          `instagram_ads_shares`,
          `instagram_ads_purchases`,
          `instagram_ads_saves`,
          `tiktok_ads_spend`,
          `tiktok_ads_follows`,
          `tiktok_ads_comments`,
          `tiktok_ads_shares`,
          `tiktok_ads_likes`,
          `tiktok_ads_purchases`,
          `pinterest_ads_spend`,
          `pinterest_ads_saves`,
          `pinterest_ads_purchases`,
          `long_term_revenue`,
        ],
      ]
    },
  },
  brand_media_optimization_ads_table: {
    enrichTags: { itemType: "ad", idKey: "ad_id" },
    q: {
      measures: [
        `company_media_metrics_ads.budget_agg`,
        `company_media_metrics_ads.lower_brand_value_agg`,
        `company_media_metrics_ads.upper_brand_value_agg`,
        `company_media_metrics_ads.revenue_from_search`,
        `company_media_metrics_ads.revenue_from_search_roas`,
        `company_media_metrics_ads.spend`,
        `company_media_metrics_ads.shares`,
        `company_media_metrics_ads.likes`,
        `company_media_metrics_ads.comments`,
        `company_media_metrics_ads.follows`,
        `company_media_metrics_ads.saves`,
        `company_media_metrics_ads.purchases`,
        `company_media_metrics_ads.brand_value_agg`,
        `company_media_metrics_ads.d30_brand_value`,
        `company_media_metrics_ads.d60_brand_value`,
        `company_media_metrics_ads.d90_brand_value`,
        `company_media_metrics_ads.facebook_ads_brand_value`,
        `company_media_metrics_ads.facebook_ads_spend`,
        `company_media_metrics_ads.facebook_ads_comments`,
        `company_media_metrics_ads.facebook_ads_shares`,
        `company_media_metrics_ads.facebook_ads_new_fans`,
        `company_media_metrics_ads.facebook_ads_reactions`,
        `company_media_metrics_ads.facebook_ads_saves`,
        `company_media_metrics_ads.facebook_ads_purchases`,
        `company_media_metrics_ads.instagram_ads_brand_value`,
        `company_media_metrics_ads.instagram_ads_spend`,
        `company_media_metrics_ads.instagram_ads_follows`,
        `company_media_metrics_ads.instagram_ads_comments`,
        `company_media_metrics_ads.instagram_ads_likes`,
        `company_media_metrics_ads.instagram_ads_shares`,
        `company_media_metrics_ads.instagram_ads_purchases`,
        `company_media_metrics_ads.instagram_ads_saves`,
        `company_media_metrics_ads.tiktok_ads_spend`,
        `company_media_metrics_ads.tiktok_ads_follows`,
        `company_media_metrics_ads.tiktok_ads_comments`,
        `company_media_metrics_ads.tiktok_ads_shares`,
        `company_media_metrics_ads.tiktok_ads_likes`,
        `company_media_metrics_ads.tiktok_ads_purchases`,
        `company_media_metrics_ads.pinterest_ads_spend`,
        `company_media_metrics_ads.pinterest_ads_saves`,
        `company_media_metrics_ads.pinterest_ads_purchases`,
        `company_media_metrics_ads.long_term_revenue`,
        `company_media_metrics_ads.short_term_revenue_roas`,
        `company_media_metrics_ads.brand_value_over_spend`,
        `company_media_metrics_ads.long_term_revenue_roas`,
        `company_media_metrics_ads.modeled_revenue`,
        `company_media_metrics_ads.modeled_revenue_over_spend`,
        `company_media_metrics_ads.estimated_d180_brand_value`,
        `company_media_metrics_ads.modeled_brand_value_over_spend`,
      ],
      dimensions: [
        `company_media_metrics_ads.media_channel`,
        `company_media_metrics_ads.campaign_name`,
        `company_media_metrics_ads.campaign_status`,
        `company_media_metrics_ads.adset_name`,
        `company_media_metrics_ads.adset_status`,
        `company_media_metrics_ads.ad_name`,
        `company_media_metrics_ads.ad_status`,
        `company_media_metrics_ads.ad_id`,
        `company_media_metrics_ads.adset_id`,
        `company_media_metrics_ads.campaign_id`,
        `company_media_metrics_ads.ad_link`,
        `company_media_metrics_ads.md_image_url`,
        `company_media_metrics_ads.initiative_type`,
      ],
      timeDimensions: [
        {
          dimension: "company_media_metrics_ads.md_date",
        },
      ],
    },
    postProcess: (data: MetricDataItem[], params: PostProcessParams) => {
      const prefix = "company_media_metrics_ads"
      const rows = data.map(item => {
        const itemWithSimpleKeys: ProcessedMetricItem = {}

        Object.keys(item).forEach(key => {
          const shortKey = key.replace(`${prefix}.`, "")
          itemWithSimpleKeys[shortKey] = item[key]
        })
        return {
          ...itemWithSimpleKeys,
          media_channel:
            MEDIA_CHANNEL_TO_SOURCE_ID_MAPPING[
              itemWithSimpleKeys.media_channel as string
            ] ?? itemWithSimpleKeys.media_channel,
        }
      })

      // Synchronous fast-path for callers that don't pass `dispatch` (e.g.
      // detail-drawer hooks calling `postProcess(pivotData, {})` casting to
      // PostProcessResultSync). Skip the asset resolve in that case.
      const dispatch = params.dispatch as AppDispatch | undefined
      if (!dispatch) {
        return [rows]
      }

      // Bulk-resolve ad creative images in a SINGLE request and write them
      // into the asset-cache slice. MarathonImage in the AdDetails drawer
      // reads from the same slice → instant display, no per-image fetch.
      const cachedAssetUrls =
        (params.cachedAssetUrls as Record<string, string> | undefined) ?? {}
      return resolveRowAssets(
        rows,
        {
          sourceTypeKey: "media_channel",
          externalRefIdKey: "ad_id",
          urlKey: "md_image_url",
        },
        { dispatch, cachedAssetUrls },
      ).then(() => [rows])
    },
  },
  brand_media_optimization_ads_table_summary: {
    q: {
      measures: [
        `company_media_metrics_ads.budget_agg`,
        `company_media_metrics_ads.budget_agg_count`,
        `company_media_metrics_ads.lower_brand_value_agg`,
        `company_media_metrics_ads.upper_brand_value_agg`,
        `company_media_metrics_ads.revenue_from_search`,
        `company_media_metrics_ads.spend`,
        `company_media_metrics_ads.spend_count`,
        `company_media_metrics_ads.shares`,
        `company_media_metrics_ads.shares_count`,
        `company_media_metrics_ads.likes`,
        `company_media_metrics_ads.likes_count`,
        `company_media_metrics_ads.comments`,
        `company_media_metrics_ads.comments_count`,
        `company_media_metrics_ads.follows`,
        `company_media_metrics_ads.follows_count`,
        `company_media_metrics_ads.saves`,
        `company_media_metrics_ads.saves_count`,
        `company_media_metrics_ads.purchases`,
        `company_media_metrics_ads.purchases_count`,
        `company_media_metrics_ads.brand_value_agg`,
        `company_media_metrics_ads.brand_value_agg_count`,
        `company_media_metrics_ads.modeled_revenue`,
        `company_media_metrics_ads.modeled_revenue_over_spend`,
        `company_media_metrics_ads.estimated_d180_brand_value`,
        `company_media_metrics_ads.modeled_brand_value_over_spend`,
        `company_media_metrics_ads.brand_value_over_spend`,
        `company_media_metrics_ads.short_term_revenue_roas`,
        `company_media_metrics_ads.long_term_revenue_roas`,
        `company_media_metrics_ads.d30_brand_value`,
        `company_media_metrics_ads.d60_brand_value`,
        `company_media_metrics_ads.d90_brand_value`,
        `company_media_metrics_ads.facebook_ads_brand_value`,
        `company_media_metrics_ads.facebook_ads_brand_value_count`,
        `company_media_metrics_ads.facebook_ads_spend`,
        `company_media_metrics_ads.facebook_ads_spend_count`,
        `company_media_metrics_ads.facebook_ads_comments`,
        `company_media_metrics_ads.facebook_ads_comments_count`,
        `company_media_metrics_ads.facebook_ads_shares`,
        `company_media_metrics_ads.facebook_ads_shares_count`,
        `company_media_metrics_ads.facebook_ads_new_fans`,
        `company_media_metrics_ads.facebook_ads_new_fans_count`,
        `company_media_metrics_ads.facebook_ads_reactions`,
        `company_media_metrics_ads.facebook_ads_reactions_count`,
        `company_media_metrics_ads.facebook_ads_saves`,
        `company_media_metrics_ads.facebook_ads_saves_count`,
        `company_media_metrics_ads.facebook_ads_purchases`,
        `company_media_metrics_ads.facebook_ads_purchases_count`,
        `company_media_metrics_ads.instagram_ads_brand_value`,
        `company_media_metrics_ads.instagram_ads_brand_value_count`,
        `company_media_metrics_ads.instagram_ads_spend`,
        `company_media_metrics_ads.instagram_ads_spend_count`,
        `company_media_metrics_ads.instagram_ads_follows`,
        `company_media_metrics_ads.instagram_ads_follows_count`,
        `company_media_metrics_ads.instagram_ads_comments`,
        `company_media_metrics_ads.instagram_ads_comments_count`,
        `company_media_metrics_ads.instagram_ads_likes`,
        `company_media_metrics_ads.instagram_ads_likes_count`,
        `company_media_metrics_ads.instagram_ads_shares`,
        `company_media_metrics_ads.instagram_ads_shares_count`,
        `company_media_metrics_ads.instagram_ads_purchases`,
        `company_media_metrics_ads.instagram_ads_purchases_count`,
        `company_media_metrics_ads.instagram_ads_saves`,
        `company_media_metrics_ads.instagram_ads_saves_count`,
        `company_media_metrics_ads.tiktok_ads_spend`,
        `company_media_metrics_ads.tiktok_ads_spend_count`,
        `company_media_metrics_ads.tiktok_ads_follows`,
        `company_media_metrics_ads.tiktok_ads_follows_count`,
        `company_media_metrics_ads.tiktok_ads_comments`,
        `company_media_metrics_ads.tiktok_ads_comments_count`,
        `company_media_metrics_ads.tiktok_ads_shares`,
        `company_media_metrics_ads.tiktok_ads_shares_count`,
        `company_media_metrics_ads.tiktok_ads_likes`,
        `company_media_metrics_ads.tiktok_ads_likes_count`,
        `company_media_metrics_ads.tiktok_ads_purchases`,
        `company_media_metrics_ads.pinterest_ads_spend`,
        `company_media_metrics_ads.pinterest_ads_saves`,
        `company_media_metrics_ads.pinterest_ads_purchases`,
        `company_media_metrics_ads.tiktok_ads_purchases_count`,
        `company_media_metrics_ads.pinterest_ads_spend_count`,
        `company_media_metrics_ads.pinterest_ads_saves_count`,
        `company_media_metrics_ads.pinterest_ads_purchases_count`,
        `company_media_metrics_ads.long_term_revenue`,
        `company_media_metrics_ads.long_term_revenue_count`,
      ],
      timeDimensions: [
        {
          dimension: "company_media_metrics_ads.md_date",
        },
      ],
    },
    postProcess: (data: MetricDataItem[]) => {
      const prefix = `company_media_metrics_ads`
      return [
        data.map(item => {
          const itemWithSimpleKeys: ProcessedMetricItem = {}

          Object.keys(item).forEach(key => {
            const shortKey = key.replace(`${prefix}.`, "")
            itemWithSimpleKeys[shortKey] = item[key]
          })
          return {
            ...itemWithSimpleKeys,
            brand_value: itemWithSimpleKeys.brand_value_agg,
          }
        }),
        [
          `budget_agg`,
          `lower_brand_value_agg`,
          `upper_brand_value_agg`,
          `revenue_from_search`,
          `revenue_from_search_roas`,
          `spend`,
          `shares`,
          `likes`,
          `comments`,
          `follows`,
          `saves`,
          `purchases`,
          `brand_value_agg`,
          `d30_brand_value`,
          `d60_brand_value`,
          `d90_brand_value`,
          `facebook_ads_brand_value`,
          `facebook_ads_spend`,
          `facebook_ads_comments`,
          `facebook_ads_shares`,
          `facebook_ads_new_fans`,
          `facebook_ads_reactions`,
          `facebook_ads_saves`,
          `facebook_ads_purchases`,
          `instagram_ads_brand_value`,
          `instagram_ads_spend`,
          `instagram_ads_follows`,
          `instagram_ads_comments`,
          `instagram_ads_likes`,
          `instagram_ads_shares`,
          `instagram_ads_purchases`,
          `instagram_ads_saves`,
          `tiktok_ads_spend`,
          `tiktok_ads_follows`,
          `tiktok_ads_comments`,
          `tiktok_ads_shares`,
          `tiktok_ads_likes`,
          `tiktok_ads_purchases`,
          `pinterest_ads_spend`,
          `pinterest_ads_saves`,
          `pinterest_ads_purchases`,
          `long_term_revenue`,
        ],
      ]
    },
  },
  brand_media_organic_social_posts_table: {
    q: {
      dimensions: [
        "company_media_metrics_organic_posts.post_id",
        "company_media_metrics_organic_posts.media_channel",
        "company_media_metrics_organic_posts.post_preview",
        "company_media_metrics_organic_posts.post_caption",
        "company_media_metrics_organic_posts.post_link",
        "company_media_metrics_organic_posts.post_date",
      ],
      measures: [
        "company_media_metrics_organic_posts.brand_value_agg",
        "company_media_metrics_organic_posts.lower_brand_value_agg",
        "company_media_metrics_organic_posts.upper_brand_value_agg",
        "company_media_metrics_organic_posts.comments",
        "company_media_metrics_organic_posts.follows",
        "company_media_metrics_organic_posts.likes",
        "company_media_metrics_organic_posts.reach",
        "company_media_metrics_organic_posts.saves",
        "company_media_metrics_organic_posts.shares",
        "company_media_metrics_organic_posts.brand_value_reach",
        "company_media_metrics_organic_posts.comments_reach",
        "company_media_metrics_organic_posts.likes_reach",
        "company_media_metrics_organic_posts.saves_reach",
        "company_media_metrics_organic_posts.shares_reach",
      ],
      timeDimensions: [
        {
          dimension: "company_media_metrics_organic_posts.md_date",
        },
      ],
    },
    postProcess: (data: MetricDataItem[], params: PostProcessParams) => {
      const savesKey = "company_media_metrics_organic_posts.saves"
      const followsKey = "company_media_metrics_organic_posts.follows"
      const mediaChannelKey =
        "company_media_metrics_organic_posts.media_channel"
      const brandValueKey =
        "company_media_metrics_organic_posts.brand_value_agg"
      const brandValueReachKey =
        "company_media_metrics_organic_posts.brand_value_reach"
      const likesReachKey = "company_media_metrics_organic_posts.likes_reach"
      const commentsReachKey =
        "company_media_metrics_organic_posts.comments_reach"
      const sharesReachKey = "company_media_metrics_organic_posts.shares_reach"
      const savesReachKey = "company_media_metrics_organic_posts.saves_reach"
      const reachKey = "company_media_metrics_organic_posts.reach"

      const transformed = data.map(item => ({
        ...item,
        [brandValueReachKey]:
          item[brandValueKey] === null || (item[reachKey] as number) === 0
            ? null
            : (item[brandValueReachKey] as number) * 1000,
        [likesReachKey]:
          (item[reachKey] as number) === 0
            ? null
            : (item[likesReachKey] as number) * 1000,
        [commentsReachKey]:
          (item[reachKey] as number) === 0
            ? null
            : (item[commentsReachKey] as number) * 1000,
        [sharesReachKey]:
          (item[reachKey] as number) === 0
            ? null
            : (item[sharesReachKey] as number) * 1000,
        [savesReachKey]:
          (item[reachKey] as number) === 0
            ? null
            : (item[savesReachKey] as number) * 1000,
        [savesKey]: item[mediaChannelKey] === "tiktok" ? "--" : item[savesKey],
        [followsKey]:
          item[mediaChannelKey] === "tiktok" ? "--" : item[followsKey],
      }))

      // Synchronous fast-path for callers without `dispatch` (e.g. AdForge's
      // useTopOrganicPosts which casts to PostProcessResultSync). Skip the
      // asset resolve in that case.
      const dispatch = params.dispatch as AppDispatch | undefined
      if (!dispatch) {
        return [transformed]
      }

      // Bulk-resolve organic post images in a SINGLE request and write the
      // URLs into the asset-cache slice. MarathonImage reads from the same
      // slice, so the Post Preview cell renders straight from cache — no
      // per-cell fetch.
      const cachedAssetUrls =
        (params.cachedAssetUrls as Record<string, string> | undefined) ?? {}
      return resolveRowAssets(
        transformed,
        {
          sourceTypeKey: mediaChannelKey,
          externalRefIdKey: "company_media_metrics_organic_posts.post_id",
          isOrganic: true,
        },
        { dispatch, cachedAssetUrls },
      ).then(() => [transformed])
    },
  },
  brand_media_organic_social_posts_table_summary: {
    q: {
      measures: [
        "company_media_metrics_organic_posts.brand_value_agg",
        "company_media_metrics_organic_posts.brand_value_agg_count",
        "company_media_metrics_organic_posts.lower_brand_value_agg",
        "company_media_metrics_organic_posts.upper_brand_value_agg",
        "company_media_metrics_organic_posts.comments",
        "company_media_metrics_organic_posts.comments_count",
        "company_media_metrics_organic_posts.follows",
        "company_media_metrics_organic_posts.follows_count",
        "company_media_metrics_organic_posts.likes",
        "company_media_metrics_organic_posts.likes_count",
        "company_media_metrics_organic_posts.reach",
        "company_media_metrics_organic_posts.reach_count",
        "company_media_metrics_organic_posts.saves",
        "company_media_metrics_organic_posts.saves_count",
        "company_media_metrics_organic_posts.shares",
        "company_media_metrics_organic_posts.shares_count",
        "company_media_metrics_organic_posts.comments_reach",
        "company_media_metrics_organic_posts.likes_reach",
        "company_media_metrics_organic_posts.saves_reach",
        "company_media_metrics_organic_posts.shares_reach",
      ],
      timeDimensions: [
        {
          dimension: "company_media_metrics_organic_posts.md_date",
        },
      ],
    },
    postProcess: (data: MetricDataItem[]) => {
      const brandValueKeyAgg =
        "company_media_metrics_organic_posts.brand_value_agg"
      const brandValueReachKey =
        "company_media_metrics_organic_posts.brand_value_reach"
      const likesReachKey = "company_media_metrics_organic_posts.likes_reach"
      const commentsReachKey =
        "company_media_metrics_organic_posts.comments_reach"
      const sharesReachKey = "company_media_metrics_organic_posts.shares_reach"
      const savesReachKey = "company_media_metrics_organic_posts.saves_reach"
      const reachKey = "company_media_metrics_organic_posts.reach"

      return [
        data.map(item => ({
          ...item,
          [brandValueReachKey]:
            safeDivide(
              item[brandValueKeyAgg] as number,
              item[reachKey] as number,
            ) * 1000,
          [likesReachKey]:
            (item[reachKey] as number) === 0
              ? null
              : (item[likesReachKey] as number) * 1000,
          [commentsReachKey]:
            (item[reachKey] as number) === 0
              ? null
              : (item[commentsReachKey] as number) * 1000,
          [sharesReachKey]:
            (item[reachKey] as number) === 0
              ? null
              : (item[sharesReachKey] as number) * 1000,
          [savesReachKey]:
            (item[reachKey] as number) === 0
              ? null
              : (item[savesReachKey] as number) * 1000,
        })),
        [
          "company_media_metrics_organic_posts.brand_value_agg",
          "company_media_metrics_organic_posts.comments",
          "company_media_metrics_organic_posts.follows",
          "company_media_metrics_organic_posts.likes",
          "company_media_metrics_organic_posts.reach",
          "company_media_metrics_organic_posts.saves",
          "company_media_metrics_organic_posts.shares",
        ],
      ]
    },
  },
  brand_media_media_mentions_table: {
    q: {
      dimensions: [
        "company_media_metrics_mentions.post_id",
        "company_media_metrics_mentions.media_channel",
        "company_media_metrics_mentions.post_caption",
        "company_media_metrics_mentions.post_link",
        "company_media_metrics_mentions.md_date",
        "company_media_metrics_mentions.creator_name",
        "company_media_metrics_mentions.display_url",
        "company_media_metrics_mentions.avatar_url",
      ],
      measures: [
        "company_media_metrics_mentions.comments",
        "company_media_metrics_mentions.followers",
        "company_media_metrics_mentions.likes",
        "company_media_metrics_mentions.reach",
        "company_media_metrics_mentions.saves",
        "company_media_metrics_mentions.shares",
        "company_media_metrics_mentions.brand_value_reach",
        "company_media_metrics_mentions.brand_value_agg",
      ],
      timeDimensions: [
        {
          dimension: "company_media_metrics_mentions.md_date",
        },
      ],
    },
    postProcess: (data: MetricDataItem[], params: PostProcessParams) => {
      const savesKey = "company_media_metrics_mentions.saves"
      const followersKey = "company_media_metrics_mentions.followers"
      const mediaChannelKey = "company_media_metrics_mentions.media_channel"
      const brandValueReachKey =
        "company_media_metrics_mentions.brand_value_reach"
      const reachKey = "company_media_metrics_mentions.reach"
      const brandValueKey = "company_media_metrics_mentions.brand_value_agg"

      const transformed = data.map(item => ({
        ...item,
        [brandValueReachKey]:
          item[brandValueKey] === null || item[reachKey] === 0
            ? null
            : item[brandValueReachKey],
        [savesKey]: item[mediaChannelKey] === "tiktok" ? "--" : item[savesKey],
        [followersKey]:
          item[mediaChannelKey] === "tiktok" ? "--" : item[followersKey],
      }))

      // Synchronous fast-path for callers without `dispatch` (legacy detail
      // hooks casting to PostProcessResultSync). Skip the asset resolve.
      const dispatch = params.dispatch as AppDispatch | undefined
      if (!dispatch) {
        return [transformed]
      }

      // Bulk-resolve mention images in a SINGLE request and write the URLs
      // into the asset-cache slice. MarathonImage reads from the same slice,
      // so the mention preview cell renders straight from cache — no per-cell
      // fetch.
      const cachedAssetUrls =
        (params.cachedAssetUrls as Record<string, string> | undefined) ?? {}
      return resolveRowAssets(
        transformed,
        {
          sourceTypeKey: mediaChannelKey,
          externalRefIdKey: "company_media_metrics_mentions.post_id",
          urlKey: "company_media_metrics_mentions.display_url",
          isOrganic: true,
        },
        { dispatch, cachedAssetUrls },
      ).then(() => [transformed])
    },
  },
  brand_media_media_mentions_table_summary: {
    q: {
      measures: [
        "company_media_metrics_mentions.brand_value_agg",
        "company_media_metrics_mentions.brand_value_agg_count",
        "company_media_metrics_mentions.comments",
        "company_media_metrics_mentions.comments_count",
        "company_media_metrics_mentions.followers",
        "company_media_metrics_mentions.followers_count",
        "company_media_metrics_mentions.likes",
        "company_media_metrics_mentions.likes_count",
        "company_media_metrics_mentions.reach",
        "company_media_metrics_mentions.reach_count",
        "company_media_metrics_mentions.saves",
        "company_media_metrics_mentions.saves_count",
        "company_media_metrics_mentions.shares",
        "company_media_metrics_mentions.shares_count",
      ],
      timeDimensions: [
        {
          dimension: "company_media_metrics_mentions.md_date",
        },
      ],
    },
    postProcess(data: MetricDataItem[]) {
      return [
        data,
        [
          "company_media_metrics_mentions.brand_value_agg",
          "company_media_metrics_mentions.comments",
          "company_media_metrics_mentions.followers",
          "company_media_metrics_mentions.likes",
          "company_media_metrics_mentions.reach",
          "company_media_metrics_mentions.saves",
          "company_media_metrics_mentions.shares",
        ],
      ]
    },
  },
  revenue_kpi_table: {
    q: (params?: {
      granularity?: TimeDimensionGranularity
      depVarScaler?: string[]
      /**
       * Explicit [[comparisonStart, comparisonEnd], [currentStart, currentEnd]]
       * windows ('YYYY-MM-DD', compareDateRange order). The KPI page's
       * useConnect always passes the buildKpiWindows output here so all five
       * KPI pipeline queries share one window computation, and arbitrary
       * Explorer date/compare windows flow through this same query builder.
       * When absent, the fixed trailing windows below are derived —
       * byte-identical to buildKpiWindows' defaults (the
       * kpi-compare-date-range spec pins the two builders together).
       */
      compareDateRange?: [[string, string], [string, string]]
    }) => {
      const granularity = params?.granularity
      const depVarScaler = params?.depVarScaler || []

      let currentStartStr: string
      let currentEndStr: string
      let comparisonStartStr: string
      let comparisonEndStr: string

      if (params?.compareDateRange) {
        const [comparisonRange, currentRange] = params.compareDateRange
        comparisonStartStr = comparisonRange[0]
        comparisonEndStr = comparisonRange[1]
        currentStartStr = currentRange[0]
        currentEndStr = currentRange[1]
      } else {
        const now = new Date()

        let currentStart: Date
        let currentEnd: Date
        let comparisonStart: Date
        let comparisonEnd: Date

        // These windows are mirrored per granularity by getKpiCompareDateRange
        // (pages/Revenue/KPI/utils/kpi-compare-date-range.ts). The KPI page's
        // secondary queries join to table columns positionally, so their windows
        // must stay identical to these; the kpi-compare-date-range spec pins the
        // two builders together and fails if this block changes without a
        // matching update there.
        if (granularity === "day") {
          // Daily: last 60 days ending yesterday; comparison is the SAME 60
          // calendar days one year earlier. The YoY merge pairs daily buckets by
          // month+day (getYoyComparisonKey), so the shift must preserve the
          // calendar date — a fixed 364/365-day offset would misalign every
          // month+day key and no bucket would ever find its counterpart.
          currentEnd = new Date(now)
          currentEnd.setDate(currentEnd.getDate() - 1) // Yesterday

          currentStart = new Date(currentEnd)
          currentStart.setDate(currentStart.getDate() - 59) // 60 days ago from yesterday

          comparisonStart = new Date(currentStart)
          comparisonStart.setFullYear(comparisonStart.getFullYear() - 1)
          comparisonEnd = new Date(currentEnd)
          comparisonEnd.setFullYear(comparisonEnd.getFullYear() - 1)
        } else if (granularity === "month") {
          // Monthly: Last 12 months. Both ranges are aligned to month
          // boundaries so every month bucket holds full-month data — except
          // the in-progress current month. The comparison range extends
          // through the last day of the prior-year current month so the
          // (partial) current month's YoY compares against the FULL
          // corresponding month from last year, not just the same MTD slice.
          currentEnd = now
          currentStart = new Date(now.getFullYear(), now.getMonth() - 12, 1)

          comparisonStart = new Date(now.getFullYear() - 2, now.getMonth(), 1)
          comparisonEnd = new Date(now.getFullYear() - 1, now.getMonth() + 1, 0)
        } else {
          // Weekly (default): 52 weeks, same as before
          currentEnd = now
          currentStart = new Date(now)
          currentStart.setDate(currentStart.getDate() - 52 * 7)

          comparisonStart = new Date(currentStart)
          comparisonStart.setDate(comparisonStart.getDate() - 52 * 7)
          comparisonEnd = new Date(currentStart)
        }

        // Serialize with LOCAL date components. toISOString converts to UTC
        // first, which shifts the calendar day for any viewer off UTC: west of
        // UTC an evening visit would include today's partial day as the last
        // "daily" bucket; east of UTC the month-aligned windows would start and
        // end a day early (shaving the last day off the prior-year month).
        const toDateStr = (d: Date) =>
          `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`
        currentEndStr = toDateStr(currentEnd)
        currentStartStr = toDateStr(currentStart)
        comparisonStartStr = toDateStr(comparisonStart)
        comparisonEndStr = toDateStr(comparisonEnd)
      }

      // Reach is non-additive: only the granularity-matching
      // facebook_ads_{daily,weekly,monthly}_reach* measures are kept; the
      // other two grains would be discarded by postProcess anyway.
      const measures = filterFbReachMeasuresByGranularity(
        REVENUE_KPI_MEASURES,
        granularity,
      )

      return {
        measures,
        ...(depVarScaler.length > 0
          ? {
              filters: [
                {
                  and: [
                    {
                      dimension: "metrics.dep_var_scaler",
                      operator: "equals",
                      values: depVarScaler,
                    },
                  ],
                },
              ],
            }
          : {}),
        timeDimensions: [
          {
            dimension: "metrics.date",
            granularity: (() => {
              if (granularity === "day") return "day"
              if (granularity === "month") return "month"
              return "week"
            })(),
            compareDateRange: [
              [comparisonStartStr, comparisonEndStr],
              [currentStartStr, currentEndStr],
            ],
          },
        ],
      }
    },
    postProcess: (
      data: MetricDataItem[],
      params: { granularity?: TimeDimensionGranularity },
    ) => {
      const { granularity } = params ?? { granularity: "week" }

      // Facebook Ads reach/impressions/spend/clicks are fetched as
      // granularity-specific raw measures and remapped onto synthetic,
      // granularity-agnostic keys (metrics.facebook_ads_reach{suffix}, etc.).
      // The empty suffix is the total account; the rest are audience segments.
      const fbAdsSegmentSuffixes = [
        "",
        "_prospecting",
        "_engaged",
        "_existing",
        "_unknown",
        "_uncategorized",
      ]

      // Reach is non-additive, so each granularity reads its dedicated
      // metric. Additive metrics (impressions, spend, link clicks) are
      // summed from the daily measures at any granularity.
      const reachSourceByGranularity = (() => {
        if (granularity === "month") return "monthly_reach"
        if (granularity === "day") return "daily_reach"
        // Default to weekly for "week" or other granularities
        return "weekly_reach"
      })()

      const processedData = data.map((item: MetricDataItem) => {
        const processedItem = { ...item }

        for (const suffix of fbAdsSegmentSuffixes) {
          processedItem[`metrics.facebook_ads_reach${suffix}`] =
            item[`metrics.facebook_ads_${reachSourceByGranularity}${suffix}`]
          processedItem[`compared.metrics.facebook_ads_reach${suffix}`] =
            item[
              `compared.metrics.facebook_ads_${reachSourceByGranularity}${suffix}`
            ]

          const additiveMetrics =
            suffix === ""
              ? ["impressions"]
              : ["impressions", "spend", "link_clicks"]
          for (const metric of additiveMetrics) {
            processedItem[`metrics.facebook_ads_${metric}${suffix}`] =
              item[`metrics.facebook_ads_daily_${metric}${suffix}`]
            processedItem[`compared.metrics.facebook_ads_${metric}${suffix}`] =
              item[`compared.metrics.facebook_ads_daily_${metric}${suffix}`]
          }

          // Remove the granularity-specific raw metrics (and their
          // comparison counterparts) to avoid confusion
          for (const rawMetric of [
            "daily_reach",
            "weekly_reach",
            "monthly_reach",
            "daily_impressions",
            ...(suffix === "" ? [] : ["daily_spend", "daily_link_clicks"]),
          ]) {
            delete processedItem[`metrics.facebook_ads_${rawMetric}${suffix}`]
            delete processedItem[
              `compared.metrics.facebook_ads_${rawMetric}${suffix}`
            ]
          }
        }

        return processedItem
      })

      return [processedData]
    },
  },
  campaign_options_query: {
    q: {
      measures: [],
      dimensions: [
        "company_media_metrics_adsets.campaign_name",
        "company_media_metrics_adsets.campaign_id",
        "company_media_metrics_adsets.adset_id",
      ],
      timeDimensions: [
        {
          dimension: "company_media_metrics_adsets.md_date",
        },
      ],
    },
  },
  // Web Merchandising - Collection Funnel
  collection_funnel_summary: {
    q: {
      measures: [
        "company_web_analytics_collection_funnel.collection_viewers",
        "company_web_analytics_collection_funnel.adders_to_cart",
        "company_web_analytics_collection_funnel.purchasers",
        "company_web_analytics_collection_funnel.first_orders",
        "company_web_analytics_collection_funnel.repeat_orders",
        "company_web_analytics_collection_funnel.total_revenue",
      ],
      timeDimensions: [
        {
          dimension: "company_web_analytics_collection_funnel.md_date",
        },
      ],
    },
  },
  company_web_analytics_page_path_report_table: {
    q: {
      measures: [
        // GA Web Analytics
        "company_web_analytics_page_path_report.sessions",
        "company_web_analytics_page_path_report.screen_page_views",
        "company_web_analytics_page_path_report.add_to_carts",
        "company_web_analytics_page_path_report.scrolled_users",
        "company_web_analytics_page_path_report.bounces",
        "company_web_analytics_page_path_report.checkouts",
        "company_web_analytics_page_path_report.key_events",
        "company_web_analytics_page_path_report.total_revenue",
        // Calculated rates
        "company_web_analytics_page_path_report.total_orders",
        "company_web_analytics_page_path_report.conversion_rate",
        "company_web_analytics_page_path_report.bounce_rate",
        "company_web_analytics_page_path_report.add_to_cart_rate",
        "company_web_analytics_page_path_report.scroll_rate",
        "company_web_analytics_page_path_report.checkout_rate",
        "company_web_analytics_page_path_report.revenue_per_session",
        "company_web_analytics_page_path_report.revenue_per_page_view",
        "company_web_analytics_page_path_report.page_views_per_session",
        "company_web_analytics_page_path_report.first_order_aov",
        "company_web_analytics_page_path_report.second_order_aov",
        // Order counts
        "company_web_analytics_page_path_report.first_orders",
        "company_web_analytics_page_path_report.second_orders",
        "company_web_analytics_page_path_report.third_orders",
        "company_web_analytics_page_path_report.fourth_orders",
        "company_web_analytics_page_path_report.fifth_plus_orders",
        "company_web_analytics_page_path_report.repeat_orders",
        // Order revenue
        "company_web_analytics_page_path_report.sum_first_order_revs",
        "company_web_analytics_page_path_report.sum_second_order_revs",
        "company_web_analytics_page_path_report.sum_third_order_revs",
        "company_web_analytics_page_path_report.sum_fourth_order_revs",
        "company_web_analytics_page_path_report.sum_fifth_plus_order_revs",
        // Order line revenue
        "company_web_analytics_page_path_report.sum_first_order_line_revenue",
        "company_web_analytics_page_path_report.sum_second_order_line_revenue",
        "company_web_analytics_page_path_report.sum_third_order_line_revenue",
        "company_web_analytics_page_path_report.sum_fourth_order_line_revenue",
        "company_web_analytics_page_path_report.sum_fifth_plus_order_line_revenue",
        // Sum LTV 90 day
        "company_web_analytics_page_path_report.sum_first_order_ltv_90_day",
        "company_web_analytics_page_path_report.sum_second_order_ltv_90_day",
        "company_web_analytics_page_path_report.sum_third_order_ltv_90_day",
        "company_web_analytics_page_path_report.sum_fourth_order_ltv_90_day",
        "company_web_analytics_page_path_report.sum_fifth_plus_order_ltv_90_day",
        // Sum LTV 180 day
        "company_web_analytics_page_path_report.sum_first_order_ltv_180_day",
        "company_web_analytics_page_path_report.sum_second_order_ltv_180_day",
        "company_web_analytics_page_path_report.sum_third_order_ltv_180_day",
        "company_web_analytics_page_path_report.sum_fourth_order_ltv_180_day",
        "company_web_analytics_page_path_report.sum_fifth_plus_order_ltv_180_day",
        // Sum LTV 365 day
        "company_web_analytics_page_path_report.sum_first_order_ltv_365_day",
        "company_web_analytics_page_path_report.sum_second_order_ltv_365_day",
        "company_web_analytics_page_path_report.sum_third_order_ltv_365_day",
        "company_web_analytics_page_path_report.sum_fourth_order_ltv_365_day",
        "company_web_analytics_page_path_report.sum_fifth_plus_order_ltv_365_day",
        // Avg LTV 90 day
        "company_web_analytics_page_path_report.avg_first_order_ltv_90_day",
        "company_web_analytics_page_path_report.avg_second_order_ltv_90_day",
        "company_web_analytics_page_path_report.avg_third_order_ltv_90_day",
        "company_web_analytics_page_path_report.avg_fourth_order_ltv_90_day",
        "company_web_analytics_page_path_report.avg_fifth_plus_order_ltv_90_day",
        // LTV as % of AOV
        "company_web_analytics_page_path_report.first_order_ltv_90_day_pct_of_aov",
        "company_web_analytics_page_path_report.expected_new_customer_session_ltv",
        // Pre-marketing contribution
        "company_web_analytics_page_path_report.sum_first_order_pre_mktg_contribution",
        "company_web_analytics_page_path_report.sum_second_order_pre_mktg_contribution",
        "company_web_analytics_page_path_report.sum_third_order_pre_mktg_contribution",
        "company_web_analytics_page_path_report.sum_fourth_order_pre_mktg_contribution",
        "company_web_analytics_page_path_report.sum_fifth_plus_order_pre_mktg_contribution",
        // Pre-marketing contribution 90 day
        "company_web_analytics_page_path_report.sum_first_order_pre_mktg_contribution_90_day",
        "company_web_analytics_page_path_report.sum_second_order_pre_mktg_contribution_90_day",
        "company_web_analytics_page_path_report.sum_third_order_pre_mktg_contribution_90_day",
        "company_web_analytics_page_path_report.sum_fourth_order_pre_mktg_contribution_90_day",
        "company_web_analytics_page_path_report.sum_fifth_plus_order_pre_mktg_contribution_90_day",
        // Pre-marketing contribution 180 day
        "company_web_analytics_page_path_report.sum_first_order_pre_mktg_contribution_180_day",
        "company_web_analytics_page_path_report.sum_second_order_pre_mktg_contribution_180_day",
        "company_web_analytics_page_path_report.sum_third_order_pre_mktg_contribution_180_day",
        "company_web_analytics_page_path_report.sum_fourth_order_pre_mktg_contribution_180_day",
        "company_web_analytics_page_path_report.sum_fifth_plus_order_pre_mktg_contribution_180_day",
        // Pre-marketing contribution 365 day
        "company_web_analytics_page_path_report.sum_first_order_pre_mktg_contribution_365_day",
        "company_web_analytics_page_path_report.sum_second_order_pre_mktg_contribution_365_day",
        "company_web_analytics_page_path_report.sum_third_order_pre_mktg_contribution_365_day",
        "company_web_analytics_page_path_report.sum_fourth_order_pre_mktg_contribution_365_day",
        "company_web_analytics_page_path_report.sum_fifth_plus_order_pre_mktg_contribution_365_day",
      ],
      dimensions: [
        "company_web_analytics_page_path_report.title",
        "company_web_analytics_page_path_report.handle",
      ],
      timeDimensions: [
        {
          dimension: "company_web_analytics_page_path_report.md_date",
        },
      ],
    },
    postProcess(data: MetricDataItem[]) {
      const processedData = data.map(item => {
        const newItem = { ...item }
        const rateMetrics = [
          "company_web_analytics_page_path_report.bounce_rate",
          "company_web_analytics_page_path_report.conversion_rate",
          "company_web_analytics_page_path_report.add_to_cart_rate",
          "company_web_analytics_page_path_report.scroll_rate",
          "company_web_analytics_page_path_report.checkout_rate",
          "company_web_analytics_page_path_report.first_order_ltv_90_day_pct_of_aov",
        ]

        rateMetrics.forEach(metric => {
          if (newItem[metric] !== undefined && newItem[metric] !== null) {
            newItem[metric] = Number(newItem[metric]) / 100
          }
        })

        return newItem
      })
      return [processedData]
    },
  },
  company_web_analytics_page_path_report_chart: {
    q: {
      measures: [
        "company_web_analytics_page_path_report.screen_page_views",
        "company_web_analytics_page_path_report.add_to_carts",
        "company_web_analytics_page_path_report.first_orders",
        "company_web_analytics_page_path_report.repeat_orders",
      ],
      timeDimensions: [
        {
          dimension: "company_web_analytics_page_path_report.md_date",
          granularity: "day",
        },
      ],
    },
  },
  company_web_analytics_landing_page_report_chart: {
    q: {
      measures: [
        "company_web_analytics_landing_page_report.sessions",
        "company_web_analytics_landing_page_report.add_to_carts",
        "company_web_analytics_landing_page_report.first_orders",
        "company_web_analytics_landing_page_report.repeat_orders",
      ],
      timeDimensions: [
        {
          dimension: "company_web_analytics_landing_page_report.md_date",
          granularity: "day",
        },
      ],
    },
  },
  company_web_analytics_landing_page_report_table: {
    q: {
      measures: [
        "company_web_analytics_landing_page_report.sessions",
        "company_web_analytics_landing_page_report.key_events",
        "company_web_analytics_landing_page_report.checkouts",
        "company_web_analytics_landing_page_report.add_to_carts",
        "company_web_analytics_landing_page_report.scrolled_users",
        "company_web_analytics_landing_page_report.bounces",
        "company_web_analytics_landing_page_report.bounce_rate",
        "company_web_analytics_landing_page_report.conversion_rate",
        "company_web_analytics_landing_page_report.checkout_rate",
        "company_web_analytics_landing_page_report.add_to_cart_rate",
        "company_web_analytics_landing_page_report.scroll_rate",
        "company_web_analytics_landing_page_report.total_revenue",
        "company_web_analytics_landing_page_report.revenue_per_session",
        "company_web_analytics_landing_page_report.first_orders",
        "company_web_analytics_landing_page_report.repeat_orders",
        "company_web_analytics_landing_page_report.sum_first_order_revs",
        "company_web_analytics_landing_page_report.sum_second_order_revs",
        "company_web_analytics_landing_page_report.sum_third_order_revs",
        "company_web_analytics_landing_page_report.sum_fourth_order_revs",
        "company_web_analytics_landing_page_report.sum_fifth_plus_order_revs",
        "company_web_analytics_landing_page_report.avg_first_order_ltv_90_day",
        "company_web_analytics_landing_page_report.avg_second_order_ltv_90_day",
        "company_web_analytics_landing_page_report.avg_third_order_ltv_90_day",
        "company_web_analytics_landing_page_report.avg_fourth_order_ltv_90_day",
        "company_web_analytics_landing_page_report.avg_fifth_plus_order_ltv_90_day",
        "company_web_analytics_landing_page_report.sum_first_order_pre_mktg_contribution",
        "company_web_analytics_landing_page_report.sum_second_order_pre_mktg_contribution",
        "company_web_analytics_landing_page_report.sum_third_order_pre_mktg_contribution",
        "company_web_analytics_landing_page_report.sum_fourth_order_pre_mktg_contribution",
        "company_web_analytics_landing_page_report.sum_fifth_plus_order_pre_mktg_contribution",
      ],
      dimensions: ["company_web_analytics_landing_page_report.handle"],
      timeDimensions: [
        {
          dimension: "company_web_analytics_landing_page_report.md_date",
        },
      ],
    },
    postProcess(data: MetricDataItem[]) {
      const processedData = data.map(item => {
        const newItem = { ...item }
        const rateMetrics = [
          "company_web_analytics_landing_page_report.bounce_rate",
          "company_web_analytics_landing_page_report.conversion_rate",
          "company_web_analytics_landing_page_report.checkout_rate",
          "company_web_analytics_landing_page_report.add_to_cart_rate",
          "company_web_analytics_landing_page_report.scroll_rate",
        ]

        rateMetrics.forEach(metric => {
          if (newItem[metric] !== undefined && newItem[metric] !== null) {
            newItem[metric] = Number(newItem[metric]) / 100
          }
        })

        return newItem
      })
      return [processedData]
    },
  },
  landing_page_report_medium_options_query: {
    q: {
      measures: ["company_web_analytics_landing_page_report.sessions"],
      dimensions: ["company_web_analytics_landing_page_report.session_medium"],
      timeDimensions: [
        {
          dimension: "company_web_analytics_landing_page_report.md_date",
        },
      ],
      order: {
        "company_web_analytics_landing_page_report.sessions": "desc",
      },
    },
  },
  product_handle_options_query: {
    q: {
      measures: ["company_web_analytics_page_path_report.total_revenue"],
      dimensions: [
        "company_web_analytics_page_path_report.handle",
        "company_web_analytics_page_path_report.title",
      ],
      timeDimensions: [
        {
          dimension: "company_web_analytics_page_path_report.md_date",
        },
      ],
      order: {
        "company_web_analytics_page_path_report.total_revenue": "desc",
      },
      // Cap the filter-options payload for large catalogues; ordered by revenue desc
      // so any truncation drops the lowest-value products first.
      limit: 10000,
    },
  },
  collection_funnel_table: {
    q: {
      dimensions: [
        "company_web_analytics_collection_funnel.collection_handle",
        "company_web_analytics_collection_funnel.collection_title",
        "company_web_analytics_collection_funnel.collection_id",
      ],
      measures: [
        "company_web_analytics_collection_funnel.collection_viewers",
        "company_web_analytics_collection_funnel.adders_to_cart",
        "company_web_analytics_collection_funnel.purchasers",
        "company_web_analytics_collection_funnel.first_orders",
        "company_web_analytics_collection_funnel.repeat_orders",
        "company_web_analytics_collection_funnel.total_revenue",
        "company_web_analytics_collection_funnel.sum_first_order_revs",
        "company_web_analytics_collection_funnel.sum_second_order_revs",
        "company_web_analytics_collection_funnel.sum_third_order_revs",
        "company_web_analytics_collection_funnel.sum_fourth_order_revs",
        "company_web_analytics_collection_funnel.sum_fifth_plus_order_revs",
        "company_web_analytics_collection_funnel.avg_first_order_ltv_90_day",
        "company_web_analytics_collection_funnel.avg_second_order_ltv_90_day",
        "company_web_analytics_collection_funnel.avg_third_order_ltv_90_day",
        "company_web_analytics_collection_funnel.avg_fourth_order_ltv_90_day",
        "company_web_analytics_collection_funnel.avg_fifth_plus_order_ltv_90_day",
        "company_web_analytics_collection_funnel.sum_first_order_pre_mktg_contribution",
        "company_web_analytics_collection_funnel.sum_second_order_pre_mktg_contribution",
        "company_web_analytics_collection_funnel.sum_third_order_pre_mktg_contribution",
        "company_web_analytics_collection_funnel.sum_fourth_order_pre_mktg_contribution",
        "company_web_analytics_collection_funnel.sum_fifth_plus_order_pre_mktg_contribution",
        "company_web_analytics_collection_funnel.total_orders",
        "company_web_analytics_collection_funnel.estimated_revenue",
        "company_web_analytics_collection_funnel.estimated_per_view_value",
      ],
      timeDimensions: [
        {
          dimension: "company_web_analytics_collection_funnel.md_date",
        },
      ],
      order: {
        "company_web_analytics_collection_funnel.collection_viewers": "desc",
      },
    },
    postProcess(data: MetricDataItem[]) {
      const P = "company_web_analytics_collection_funnel"
      const get = (row: MetricDataItem, k: string) =>
        Number(row[`${P}.${k}`] ?? 0)
      const pct = (a: number, b: number) => (b > 0 ? (a / b) * 100 : 0)
      return [
        data.map((row: MetricDataItem) => {
          const collectionViewers = get(row, "collection_viewers")
          const addersToCart = get(row, "adders_to_cart")
          const purchasers = get(row, "purchasers")
          const collectionToCartConversion = pct(
            addersToCart,
            collectionViewers,
          )
          const cartToPurchaseConversion = pct(purchasers, addersToCart)
          const collectionToPurchaseConversion = pct(
            purchasers,
            collectionViewers,
          )
          return {
            id: String(row[`${P}.collection_id`] ?? ""),
            collection_handle: String(row[`${P}.collection_handle`] ?? ""),
            collection_title: String(
              row[`${P}.collection_title`] ?? "Unnamed Collection",
            ),
            collection_id: String(row[`${P}.collection_id`] ?? ""),
            collection_viewers: collectionViewers,
            adders_to_cart: addersToCart,
            purchasers,
            collection_to_cart_conversion:
              collectionToCartConversion.toFixed(2),
            collection_to_cart_abandon: (
              100 - collectionToCartConversion
            ).toFixed(2),
            cart_to_purchase_conversion: cartToPurchaseConversion.toFixed(2),
            cart_to_purchase_abandon: (100 - cartToPurchaseConversion).toFixed(
              2,
            ),
            collection_to_purchase_conversion:
              collectionToPurchaseConversion.toFixed(2),
            collection_to_purchase_abandon: (
              100 - collectionToPurchaseConversion
            ).toFixed(2),
            first_orders: get(row, "first_orders"),
            repeat_orders: get(row, "repeat_orders"),
            sum_first_order_revs: get(row, "sum_first_order_revs"),
            sum_second_order_revs: get(row, "sum_second_order_revs"),
            sum_third_order_revs: get(row, "sum_third_order_revs"),
            sum_fourth_order_revs: get(row, "sum_fourth_order_revs"),
            sum_fifth_plus_order_revs: get(row, "sum_fifth_plus_order_revs"),
            avg_first_order_ltv_90_day: get(row, "avg_first_order_ltv_90_day"),
            avg_second_order_ltv_90_day: get(
              row,
              "avg_second_order_ltv_90_day",
            ),
            avg_third_order_ltv_90_day: get(row, "avg_third_order_ltv_90_day"),
            avg_fourth_order_ltv_90_day: get(
              row,
              "avg_fourth_order_ltv_90_day",
            ),
            avg_fifth_plus_order_ltv_90_day: get(
              row,
              "avg_fifth_plus_order_ltv_90_day",
            ),
            sum_first_order_pre_mktg_contribution: get(
              row,
              "sum_first_order_pre_mktg_contribution",
            ),
            sum_second_order_pre_mktg_contribution: get(
              row,
              "sum_second_order_pre_mktg_contribution",
            ),
            sum_third_order_pre_mktg_contribution: get(
              row,
              "sum_third_order_pre_mktg_contribution",
            ),
            sum_fourth_order_pre_mktg_contribution: get(
              row,
              "sum_fourth_order_pre_mktg_contribution",
            ),
            sum_fifth_plus_order_pre_mktg_contribution: get(
              row,
              "sum_fifth_plus_order_pre_mktg_contribution",
            ),
          }
        }),
      ]
    },
  },
  // Web Merchandising - Product Page Funnel
  product_page_funnel_summary: {
    q: {
      measures: [
        "company_web_analytics_product_page_funnel.product_viewers",
        "company_web_analytics_product_page_funnel.adders_to_cart",
        "company_web_analytics_product_page_funnel.checkouts",
        "company_web_analytics_product_page_funnel.purchasers",
        "company_web_analytics_product_page_funnel.first_orders",
        "company_web_analytics_product_page_funnel.repeat_orders",
        "company_web_analytics_product_page_funnel.total_revenue",
      ],
      timeDimensions: [
        {
          dimension: "company_web_analytics_product_page_funnel.md_date",
        },
      ],
    },
  },
  product_page_funnel_table: {
    q: {
      dimensions: [
        "company_web_analytics_product_page_funnel.product_handle",
        "company_web_analytics_product_page_funnel.product_title",
        "company_web_analytics_product_page_funnel.product_id",
      ],
      measures: [
        "company_web_analytics_product_page_funnel.product_viewers",
        "company_web_analytics_product_page_funnel.adders_to_cart",
        "company_web_analytics_product_page_funnel.checkouts",
        "company_web_analytics_product_page_funnel.purchasers",
        "company_web_analytics_product_page_funnel.first_orders",
        "company_web_analytics_product_page_funnel.repeat_orders",
        "company_web_analytics_product_page_funnel.sum_first_order_revs",
        "company_web_analytics_product_page_funnel.sum_second_order_revs",
        "company_web_analytics_product_page_funnel.sum_third_order_revs",
        "company_web_analytics_product_page_funnel.sum_fourth_order_revs",
        "company_web_analytics_product_page_funnel.sum_fifth_plus_order_revs",
        "company_web_analytics_product_page_funnel.avg_first_order_ltv_90_day",
        "company_web_analytics_product_page_funnel.avg_second_order_ltv_90_day",
        "company_web_analytics_product_page_funnel.avg_third_order_ltv_90_day",
        "company_web_analytics_product_page_funnel.avg_fourth_order_ltv_90_day",
        "company_web_analytics_product_page_funnel.avg_fifth_plus_order_ltv_90_day",
        "company_web_analytics_product_page_funnel.sum_first_order_pre_mktg_contribution",
        "company_web_analytics_product_page_funnel.sum_second_order_pre_mktg_contribution",
        "company_web_analytics_product_page_funnel.sum_third_order_pre_mktg_contribution",
        "company_web_analytics_product_page_funnel.sum_fourth_order_pre_mktg_contribution",
        "company_web_analytics_product_page_funnel.sum_fifth_plus_order_pre_mktg_contribution",
        "company_web_analytics_product_page_funnel.total_orders",
        "company_web_analytics_product_page_funnel.total_revenue",
        "company_web_analytics_product_page_funnel.estimated_revenue",
        "company_web_analytics_product_page_funnel.estimated_per_view_value",
      ],
      timeDimensions: [
        {
          dimension: "company_web_analytics_product_page_funnel.md_date",
        },
      ],
      order: {
        "company_web_analytics_product_page_funnel.product_viewers": "desc",
      },
    },
    postProcess(data: MetricDataItem[]) {
      const P = "company_web_analytics_product_page_funnel"
      const get = (row: MetricDataItem, k: string) =>
        Number(row[`${P}.${k}`] ?? 0)
      const pct = (a: number, b: number) => (b > 0 ? (a / b) * 100 : 0)
      return [
        data.map((row: MetricDataItem) => {
          const productViewers = get(row, "product_viewers")
          const addersToCart = get(row, "adders_to_cart")
          const checkouts = get(row, "checkouts")
          const purchasers = get(row, "purchasers")
          const productToCartConversion = pct(addersToCart, productViewers)
          const cartToCheckoutConversion = pct(checkouts, addersToCart)
          const checkoutToPurchaseConversion = pct(purchasers, checkouts)
          const productToPurchaseConversion = pct(purchasers, productViewers)
          return {
            id: String(row[`${P}.product_id`] ?? ""),
            product_handle: String(row[`${P}.product_handle`] ?? ""),
            product_title: String(
              row[`${P}.product_title`] ?? "Unnamed Product",
            ),
            product_id: String(row[`${P}.product_id`] ?? ""),
            product_viewers: productViewers,
            adders_to_cart: addersToCart,
            checkouts,
            purchasers,
            product_to_cart_conversion: productToCartConversion.toFixed(2),
            product_to_cart_abandon: (100 - productToCartConversion).toFixed(2),
            cart_to_checkout_conversion: cartToCheckoutConversion.toFixed(2),
            cart_to_checkout_abandon: (100 - cartToCheckoutConversion).toFixed(
              2,
            ),
            checkout_to_purchase_conversion:
              checkoutToPurchaseConversion.toFixed(2),
            checkout_to_purchase_abandon: (
              100 - checkoutToPurchaseConversion
            ).toFixed(2),
            product_to_purchase_conversion:
              productToPurchaseConversion.toFixed(2),
            product_to_purchase_abandon: (
              100 - productToPurchaseConversion
            ).toFixed(2),
            first_orders: get(row, "first_orders"),
            repeat_orders: get(row, "repeat_orders"),
            sum_first_order_revs: get(row, "sum_first_order_revs"),
            sum_second_order_revs: get(row, "sum_second_order_revs"),
            sum_third_order_revs: get(row, "sum_third_order_revs"),
            sum_fourth_order_revs: get(row, "sum_fourth_order_revs"),
            sum_fifth_plus_order_revs: get(row, "sum_fifth_plus_order_revs"),
            avg_first_order_ltv_90_day: get(row, "avg_first_order_ltv_90_day"),
            avg_second_order_ltv_90_day: get(
              row,
              "avg_second_order_ltv_90_day",
            ),
            avg_third_order_ltv_90_day: get(row, "avg_third_order_ltv_90_day"),
            avg_fourth_order_ltv_90_day: get(
              row,
              "avg_fourth_order_ltv_90_day",
            ),
            avg_fifth_plus_order_ltv_90_day: get(
              row,
              "avg_fifth_plus_order_ltv_90_day",
            ),
            sum_first_order_pre_mktg_contribution: get(
              row,
              "sum_first_order_pre_mktg_contribution",
            ),
            sum_second_order_pre_mktg_contribution: get(
              row,
              "sum_second_order_pre_mktg_contribution",
            ),
            sum_third_order_pre_mktg_contribution: get(
              row,
              "sum_third_order_pre_mktg_contribution",
            ),
            sum_fourth_order_pre_mktg_contribution: get(
              row,
              "sum_fourth_order_pre_mktg_contribution",
            ),
            sum_fifth_plus_order_pre_mktg_contribution: get(
              row,
              "sum_fifth_plus_order_pre_mktg_contribution",
            ),
          }
        }),
      ]
    },
  },
  // Web Merchandising - Homepage Funnel
  homepage_funnel_summary: {
    q: {
      measures: [
        "company_web_analytics_homepage_funnel.homepage_viewers",
        "company_web_analytics_homepage_funnel.shopping_page_viewers",
        "company_web_analytics_homepage_funnel.adders_to_cart",
        "company_web_analytics_homepage_funnel.purchasers",
      ],
      timeDimensions: [
        {
          dimension: "company_web_analytics_homepage_funnel.md_date",
        },
      ],
    },
  },
  homepage_funnel_table: {
    q: {
      dimensions: ["company_web_analytics_homepage_funnel.device_category"],
      measures: [
        "company_web_analytics_homepage_funnel.homepage_viewers",
        "company_web_analytics_homepage_funnel.shopping_page_viewers",
        "company_web_analytics_homepage_funnel.adders_to_cart",
        "company_web_analytics_homepage_funnel.purchasers",
      ],
      timeDimensions: [
        {
          dimension: "company_web_analytics_homepage_funnel.md_date",
        },
      ],
      order: [
        ["company_web_analytics_homepage_funnel.homepage_viewers", "desc"],
      ],
    },
  },
  // Home Dashboard V2 Queries
  home_dashboard_metrics: {
    q: {
      measures: [
        // Customer Acquisition - Revenue (All types)
        "metrics.shopify_total_new_customer_revenue",
        "metrics.shopify_ecomm_new_customer_revenue",
        "metrics.shopify_dtc_retail_new_customer_revenue",
        "metrics.shopify_third_party_ecomm_new_customer_revenue",
        "metrics.shopify_subscription_new_customer_revenue",
        // Retention - Revenue (All types)
        "metrics.shopify_total_repeat_customer_revenue",
        "metrics.shopify_ecomm_repeat_customer_revenue",
        "metrics.shopify_dtc_retail_repeat_customer_revenue",
        "metrics.shopify_third_party_ecomm_repeat_customer_revenue",
        "metrics.shopify_subscription_repeat_customer_revenue",
        // Total Revenue - All revenue types
        "metrics.shopify_total_customer_revenue",
        "metrics.shopify_ecomm_total_customer_revenue",
        "metrics.shopify_dtc_retail_total_customer_revenue",
        "metrics.shopify_third_party_ecomm_total_customer_revenue",
        "metrics.shopify_subscription_total_customer_revenue",
        // Brand
        "metrics.d180_brand_value",
        "metrics.google_search_organic_branded_search_impressions",
        "metrics.ga_baseline_revenue",
        "metrics.ga_total_revenue",
        // Marketing Efficiency
        "metrics.total_ad_spend",
        "metrics.amer",
        "metrics.shopify_paid_revenue",
        "metrics.ga_total_revenue", // For GA-only fallback
        "metrics.total_contribution_margin",
        "metrics.contribution_margin_percent",
        // Waterfall Chart - Revenue to Profit breakdown
        "metrics.shopify_total_revenue",
        "metrics.shopify_total_gross_profit",
        "metrics.shopify_total_order_cost",
        "metrics.amazon_ads_total_spend",
        // Sessions / Consideration
        "metrics.ga_sessions",
        "metrics.ga_baseline_sessions",
        "metrics.ga_engaged_sessions_from_new_visitors",
        // Conversion
        "metrics.total_per_session_value",
        "metrics.baseline_per_session_value",
      ],
      timeDimensions: [
        {
          dimension: "metrics.date",
        },
      ],
    },
  },
  home_dashboard_daily_chart: {
    q: {
      measures: [
        "metrics.total_ad_spend",
        // Total revenue metrics
        "metrics.shopify_total_customer_revenue",
        "metrics.shopify_total_new_customer_revenue",
        "metrics.shopify_total_repeat_customer_revenue",
        // Ecomm revenue metrics
        "metrics.shopify_ecomm_total_customer_revenue",
        "metrics.shopify_ecomm_new_customer_revenue",
        "metrics.shopify_ecomm_repeat_customer_revenue",
        // DTC Retail revenue metrics
        "metrics.shopify_dtc_retail_total_customer_revenue",
        "metrics.shopify_dtc_retail_new_customer_revenue",
        "metrics.shopify_dtc_retail_repeat_customer_revenue",
        // Third Party Ecomm revenue metrics
        "metrics.shopify_third_party_ecomm_total_customer_revenue",
        "metrics.shopify_third_party_ecomm_new_customer_revenue",
        "metrics.shopify_third_party_ecomm_repeat_customer_revenue",
        // Subscription revenue metrics
        "metrics.shopify_subscription_total_customer_revenue",
        "metrics.shopify_subscription_new_customer_revenue",
        "metrics.shopify_subscription_repeat_customer_revenue",
        // Amazon revenue (owned + ad-driven), shown beside the Amazon-inclusive spend
        "metrics.amazon_total_revenue",
        // GA revenue for fallback
        "metrics.ga_total_revenue",
        "metrics.amer",
      ],
      timeDimensions: [
        {
          dimension: "metrics.date",
          granularity: "day",
        },
      ],
      order: {
        "metrics.date": "asc",
      },
    },
  },
  home_dashboard_top_collections: {
    q: {
      dimensions: [
        "company_web_analytics_collection_funnel.collection_title",
        "company_web_analytics_collection_funnel.collection_handle",
        "company_web_analytics_collection_funnel.collection_id",
      ],
      measures: [
        "company_web_analytics_collection_funnel.collection_viewers",
        "company_web_analytics_collection_funnel.adders_to_cart",
        "company_web_analytics_collection_funnel.purchasers",
        "company_web_analytics_collection_funnel.total_revenue",
      ],
      filters: [
        {
          member: "company_web_analytics_collection_funnel.session_filter",
          operator: "equals",
          values: ["TOTAL"],
        },
      ],
      timeDimensions: [
        {
          dimension: "company_web_analytics_collection_funnel.md_date",
        },
      ],
      order: [
        ["company_web_analytics_collection_funnel.collection_viewers", "desc"],
      ],
      limit: 10,
    },
  },
  home_dashboard_top_products: {
    q: {
      dimensions: [
        "company_web_analytics_product_page_funnel.product_title",
        "company_web_analytics_product_page_funnel.product_handle",
        "company_web_analytics_product_page_funnel.product_id",
      ],
      measures: [
        "company_web_analytics_product_page_funnel.product_viewers",
        "company_web_analytics_product_page_funnel.adders_to_cart",
        "company_web_analytics_product_page_funnel.checkouts",
        "company_web_analytics_product_page_funnel.purchasers",
        "company_web_analytics_product_page_funnel.total_revenue",
      ],
      filters: [
        {
          member: "company_web_analytics_product_page_funnel.session_filter",
          operator: "equals",
          values: ["TOTAL"],
        },
      ],
      timeDimensions: [
        {
          dimension: "company_web_analytics_product_page_funnel.md_date",
        },
      ],
      order: [
        ["company_web_analytics_product_page_funnel.product_viewers", "desc"],
      ],
      limit: 10,
    },
  },

  // Mentions Analysis Charts and Tables
  mentions_analysis_time_series_chart: {
    q: {
      measures: [
        "company_ai_mentions_metrics.post_count",
        "company_ai_mentions_metrics.avg_sentiment",
        "company_ai_mentions_metrics.total_likes",
        "company_ai_mentions_metrics.total_comments",
        "company_ai_mentions_metrics.total_views",
        "company_ai_mentions_metrics.avg_likes",
        "company_ai_mentions_metrics.avg_comments",
        "company_ai_mentions_metrics.avg_views",
      ],
      timeDimensions: [
        {
          dimension: "company_ai_mentions_metrics.md_date",
        },
      ],
      order: {
        "company_ai_mentions_metrics.md_date": "asc",
      },
    },
  },

  mentions_sentiment_by_theme_chart: {
    q: {
      dimensions: ["company_ai_mentions_metrics.dimension_value"],
      measures: ["company_ai_mentions_metrics.dim_avg_sentiment"],
      filters: [
        {
          member: "company_ai_mentions_metrics.dimension_type",
          operator: "equals",
          values: ["theme"],
        },
      ],
      timeDimensions: [
        {
          dimension: "company_ai_mentions_metrics.md_date",
        },
      ],
      order: [["company_ai_mentions_metrics.dim_avg_sentiment", "desc"]],
      limit: 10,
    },
  },

  mentions_post_count_by_theme_chart: {
    q: {
      dimensions: ["company_ai_mentions_metrics.dimension_value"],
      measures: ["company_ai_mentions_metrics.post_count"],
      filters: [
        {
          member: "company_ai_mentions_metrics.dimension_type",
          operator: "equals",
          values: ["theme"],
        },
      ],
      timeDimensions: [
        {
          dimension: "company_ai_mentions_metrics.md_date",
        },
      ],
      order: [["company_ai_mentions_metrics.post_count", "desc"]],
      limit: 10,
    },
  },

  mentions_by_theme_table: {
    q: {
      dimensions: ["company_ai_mentions_metrics.dimension_value"],
      measures: [
        "company_ai_mentions_metrics.post_count",
        "company_ai_mentions_metrics.dim_avg_sentiment",
        "company_ai_mentions_metrics.dim_total_likes",
        "company_ai_mentions_metrics.dim_total_comments",
        "company_ai_mentions_metrics.dim_total_views",
        "company_ai_mentions_metrics.dim_avg_likes",
        "company_ai_mentions_metrics.dim_avg_comments",
        "company_ai_mentions_metrics.dim_avg_views",
      ],
      filters: [
        {
          member: "company_ai_mentions_metrics.dimension_type",
          operator: "equals",
          values: ["theme"],
        },
      ],
      timeDimensions: [
        {
          dimension: "company_ai_mentions_metrics.md_date",
        },
      ],
      order: [["company_ai_mentions_metrics.post_count", "desc"]],
    },
  },

  mentions_by_metatheme_table: {
    q: {
      dimensions: ["company_ai_mentions_metrics.dimension_value"],
      measures: [
        "company_ai_mentions_metrics.post_count",
        "company_ai_mentions_metrics.dim_avg_sentiment",
        "company_ai_mentions_metrics.dim_total_likes",
        "company_ai_mentions_metrics.dim_total_comments",
        "company_ai_mentions_metrics.dim_total_views",
        "company_ai_mentions_metrics.dim_avg_likes",
        "company_ai_mentions_metrics.dim_avg_comments",
        "company_ai_mentions_metrics.dim_avg_views",
      ],
      filters: [
        {
          member: "company_ai_mentions_metrics.dimension_type",
          operator: "equals",
          values: ["metatheme"],
        },
      ],
      timeDimensions: [
        {
          dimension: "company_ai_mentions_metrics.md_date",
        },
      ],
      order: [["company_ai_mentions_metrics.post_count", "desc"]],
    },
  },

  mentions_by_buying_occasion_table: {
    q: {
      dimensions: ["company_ai_mentions_metrics.dimension_value"],
      measures: [
        "company_ai_mentions_metrics.post_count",
        "company_ai_mentions_metrics.dim_avg_sentiment",
        "company_ai_mentions_metrics.dim_total_likes",
        "company_ai_mentions_metrics.dim_total_comments",
        "company_ai_mentions_metrics.dim_total_views",
        "company_ai_mentions_metrics.dim_avg_likes",
        "company_ai_mentions_metrics.dim_avg_comments",
        "company_ai_mentions_metrics.dim_avg_views",
      ],
      filters: [
        {
          member: "company_ai_mentions_metrics.dimension_type",
          operator: "equals",
          values: ["buying_occasion"],
        },
      ],
      timeDimensions: [
        {
          dimension: "company_ai_mentions_metrics.md_date",
        },
      ],
      order: [["company_ai_mentions_metrics.post_count", "desc"]],
    },
  },

  mentions_by_category_table: {
    q: {
      dimensions: ["company_ai_mentions_metrics.dimension_value"],
      measures: [
        "company_ai_mentions_metrics.post_count",
        "company_ai_mentions_metrics.dim_avg_sentiment",
        "company_ai_mentions_metrics.dim_total_likes",
        "company_ai_mentions_metrics.dim_total_comments",
        "company_ai_mentions_metrics.dim_total_views",
        "company_ai_mentions_metrics.dim_avg_likes",
        "company_ai_mentions_metrics.dim_avg_comments",
        "company_ai_mentions_metrics.dim_avg_views",
      ],
      filters: [
        {
          member: "company_ai_mentions_metrics.dimension_type",
          operator: "equals",
          values: ["category"],
        },
      ],
      timeDimensions: [
        {
          dimension: "company_ai_mentions_metrics.md_date",
        },
      ],
      order: [["company_ai_mentions_metrics.post_count", "desc"]],
    },
  },

  company_web_analytics_pre_atc_page_paths_table: {
    q: {
      measures: [
        "company_web_analytics_page_value_pre_add_to_cart_page_paths.session_count",
        "company_web_analytics_page_value_pre_add_to_cart_page_paths.user_count",
        "company_web_analytics_page_value_pre_add_to_cart_page_paths.avg_page_count",
        "company_web_analytics_page_value_pre_add_to_cart_page_paths.adds_to_cart",
        "company_web_analytics_page_value_pre_add_to_cart_page_paths.checkout_starts",
        "company_web_analytics_page_value_pre_add_to_cart_page_paths.checkout_completes",
        "company_web_analytics_page_value_pre_add_to_cart_page_paths.purchases",
        "company_web_analytics_page_value_pre_add_to_cart_page_paths.purchase_value",
        "company_web_analytics_page_value_pre_add_to_cart_page_paths.current_revenue",
        "company_web_analytics_page_value_pre_add_to_cart_page_paths.gross_revenue",
        "company_web_analytics_page_value_pre_add_to_cart_page_paths.gross_profit",
        "company_web_analytics_page_value_pre_add_to_cart_page_paths.new_customer_revenue",
        "company_web_analytics_page_value_pre_add_to_cart_page_paths.repeat_customer_revenue",
        "company_web_analytics_page_value_pre_add_to_cart_page_paths.new_customer_gross_profit",
        "company_web_analytics_page_value_pre_add_to_cart_page_paths.repeat_customer_gross_profit",
        "company_web_analytics_page_value_pre_add_to_cart_page_paths.prediction",
      ],
      dimensions: [
        "company_web_analytics_page_value_pre_add_to_cart_page_paths.pre_add_to_cart_page_path",
      ],
      timeDimensions: [
        {
          dimension:
            "company_web_analytics_page_value_pre_add_to_cart_page_paths.session_start_date",
        },
      ],
    },
  },
  company_web_analytics_pre_atc_collections_table: {
    q: {
      measures: [
        "company_web_analytics_page_value_pre_add_to_cart_collections.session_count",
        "company_web_analytics_page_value_pre_add_to_cart_collections.user_count",
        "company_web_analytics_page_value_pre_add_to_cart_collections.avg_page_count",
        "company_web_analytics_page_value_pre_add_to_cart_collections.adds_to_cart",
        "company_web_analytics_page_value_pre_add_to_cart_collections.checkout_starts",
        "company_web_analytics_page_value_pre_add_to_cart_collections.checkout_completes",
        "company_web_analytics_page_value_pre_add_to_cart_collections.purchases",
        "company_web_analytics_page_value_pre_add_to_cart_collections.purchase_value",
        "company_web_analytics_page_value_pre_add_to_cart_collections.current_revenue",
        "company_web_analytics_page_value_pre_add_to_cart_collections.gross_revenue",
        "company_web_analytics_page_value_pre_add_to_cart_collections.gross_profit",
        "company_web_analytics_page_value_pre_add_to_cart_collections.new_customer_revenue",
        "company_web_analytics_page_value_pre_add_to_cart_collections.repeat_customer_revenue",
        "company_web_analytics_page_value_pre_add_to_cart_collections.new_customer_gross_profit",
        "company_web_analytics_page_value_pre_add_to_cart_collections.repeat_customer_gross_profit",
        "company_web_analytics_page_value_pre_add_to_cart_collections.prediction",
      ],
      dimensions: [
        "company_web_analytics_page_value_pre_add_to_cart_collections.pre_add_to_cart_collection",
      ],
      timeDimensions: [
        {
          dimension:
            "company_web_analytics_page_value_pre_add_to_cart_collections.session_start_date",
        },
      ],
    },
  },
  company_web_analytics_pre_atc_products_table: {
    q: {
      measures: [
        "company_web_analytics_page_value_pre_add_to_cart_products.session_count",
        "company_web_analytics_page_value_pre_add_to_cart_products.user_count",
        "company_web_analytics_page_value_pre_add_to_cart_products.avg_page_count",
        "company_web_analytics_page_value_pre_add_to_cart_products.adds_to_cart",
        "company_web_analytics_page_value_pre_add_to_cart_products.checkout_starts",
        "company_web_analytics_page_value_pre_add_to_cart_products.checkout_completes",
        "company_web_analytics_page_value_pre_add_to_cart_products.purchases",
        "company_web_analytics_page_value_pre_add_to_cart_products.purchase_value",
        "company_web_analytics_page_value_pre_add_to_cart_products.current_revenue",
        "company_web_analytics_page_value_pre_add_to_cart_products.gross_revenue",
        "company_web_analytics_page_value_pre_add_to_cart_products.gross_profit",
        "company_web_analytics_page_value_pre_add_to_cart_products.new_customer_revenue",
        "company_web_analytics_page_value_pre_add_to_cart_products.repeat_customer_revenue",
        "company_web_analytics_page_value_pre_add_to_cart_products.new_customer_gross_profit",
        "company_web_analytics_page_value_pre_add_to_cart_products.repeat_customer_gross_profit",
        "company_web_analytics_page_value_pre_add_to_cart_products.prediction",
      ],
      dimensions: [
        "company_web_analytics_page_value_pre_add_to_cart_products.pre_add_to_cart_product",
      ],
      timeDimensions: [
        {
          dimension:
            "company_web_analytics_page_value_pre_add_to_cart_products.session_start_date",
        },
      ],
    },
  },
  company_web_analytics_page_depth_first_pageview_table: {
    q: {
      measures: [
        "company_web_analytics_page_value_first_pageview.session_count",
        "company_web_analytics_page_value_first_pageview.user_count",
        "company_web_analytics_page_value_first_pageview.avg_page_count",
        "company_web_analytics_page_value_first_pageview.adds_to_cart",
        "company_web_analytics_page_value_first_pageview.checkout_starts",
        "company_web_analytics_page_value_first_pageview.checkout_completes",
        "company_web_analytics_page_value_first_pageview.purchases",
        "company_web_analytics_page_value_first_pageview.purchase_value",
        "company_web_analytics_page_value_first_pageview.current_revenue",
        "company_web_analytics_page_value_first_pageview.gross_revenue",
        "company_web_analytics_page_value_first_pageview.gross_profit",
        "company_web_analytics_page_value_first_pageview.new_customer_revenue",
        "company_web_analytics_page_value_first_pageview.repeat_customer_revenue",
        "company_web_analytics_page_value_first_pageview.new_customer_gross_profit",
        "company_web_analytics_page_value_first_pageview.repeat_customer_gross_profit",
        "company_web_analytics_page_value_first_pageview.prediction",
      ],
      dimensions: [
        "company_web_analytics_page_value_first_pageview.first_pageview_page_path",
      ],
      timeDimensions: [
        {
          dimension:
            "company_web_analytics_page_value_first_pageview.session_start_date",
        },
      ],
    },
  },
  company_web_analytics_page_depth_second_pageview_table: {
    q: {
      measures: [
        "company_web_analytics_page_value_second_pageview.session_count",
        "company_web_analytics_page_value_second_pageview.user_count",
        "company_web_analytics_page_value_second_pageview.avg_page_count",
        "company_web_analytics_page_value_second_pageview.adds_to_cart",
        "company_web_analytics_page_value_second_pageview.checkout_starts",
        "company_web_analytics_page_value_second_pageview.checkout_completes",
        "company_web_analytics_page_value_second_pageview.purchases",
        "company_web_analytics_page_value_second_pageview.purchase_value",
        "company_web_analytics_page_value_second_pageview.current_revenue",
        "company_web_analytics_page_value_second_pageview.gross_revenue",
        "company_web_analytics_page_value_second_pageview.gross_profit",
        "company_web_analytics_page_value_second_pageview.new_customer_revenue",
        "company_web_analytics_page_value_second_pageview.repeat_customer_revenue",
        "company_web_analytics_page_value_second_pageview.new_customer_gross_profit",
        "company_web_analytics_page_value_second_pageview.repeat_customer_gross_profit",
        "company_web_analytics_page_value_second_pageview.prediction",
      ],
      dimensions: [
        "company_web_analytics_page_value_second_pageview.second_pageview_page_path",
      ],
      timeDimensions: [
        {
          dimension:
            "company_web_analytics_page_value_second_pageview.session_start_date",
        },
      ],
    },
  },
  company_web_analytics_page_depth_third_pageview_table: {
    q: {
      measures: [
        "company_web_analytics_page_value_third_pageview.session_count",
        "company_web_analytics_page_value_third_pageview.user_count",
        "company_web_analytics_page_value_third_pageview.avg_page_count",
        "company_web_analytics_page_value_third_pageview.adds_to_cart",
        "company_web_analytics_page_value_third_pageview.checkout_starts",
        "company_web_analytics_page_value_third_pageview.checkout_completes",
        "company_web_analytics_page_value_third_pageview.purchases",
        "company_web_analytics_page_value_third_pageview.purchase_value",
        "company_web_analytics_page_value_third_pageview.current_revenue",
        "company_web_analytics_page_value_third_pageview.gross_revenue",
        "company_web_analytics_page_value_third_pageview.gross_profit",
        "company_web_analytics_page_value_third_pageview.new_customer_revenue",
        "company_web_analytics_page_value_third_pageview.repeat_customer_revenue",
        "company_web_analytics_page_value_third_pageview.new_customer_gross_profit",
        "company_web_analytics_page_value_third_pageview.repeat_customer_gross_profit",
        "company_web_analytics_page_value_third_pageview.prediction",
      ],
      dimensions: [
        "company_web_analytics_page_value_third_pageview.third_pageview_page_path",
      ],
      timeDimensions: [
        {
          dimension:
            "company_web_analytics_page_value_third_pageview.session_start_date",
        },
      ],
    },
  },
  company_shopify_inventory_sku_sellouts_table: {
    q: {
      measures: [
        "company_shopify_inventory_events.sellout_count",
        "company_shopify_inventory_events.restock_count",
        "company_shopify_variant_daily_sales.units_sold",
        "company_shopify_variant_daily_sales.revenue",
      ],
      dimensions: [
        "company_shopify_inventory_events.product_title",
        "company_shopify_inventory_events.sku",
        "company_shopify_inventory_events.variant_title",
        "company_shopify_inventory_events.location_id",
        "company_shopify_inventory_events.event_type",
        "company_shopify_inventory_events.event_date",
        "company_shopify_inventory_events.is_currently_sold_out",
        "company_shopify_inventory_events.current_available",
        "company_shopify_inventory_events.available_before_event",
        "company_shopify_inventory_events.available_after_event",
        "company_shopify_inventory_events.variant_id",
        "company_shopify_inventory_events.product_id",
      ],
      timeDimensions: [
        {
          dimension: "company_shopify_inventory_events.event_date",
        },
        {
          // Bounds units_sold / revenue (from company_shopify_variant_daily_sales)
          // to the selected date range, so they show the variant's sales over the
          // period rather than all-time. The same dateRange is applied to every
          // timeDimension by polyfillCubeQueryWithTimeDimension.
          dimension: "company_shopify_variant_daily_sales.md_date",
        },
      ],
    },
  },
  company_shopify_inventory_product_breakage_table: {
    q: {
      measures: [
        "company_shopify_product_variant_breakage.sum_broken_variants",
        "company_shopify_product_variant_breakage.sum_total_variants",
        "company_shopify_product_variant_breakage.avg_breakage_rate",
        "company_shopify_product_variant_breakage.sum_sellout_events",
        "company_shopify_product_variant_breakage.sum_restock_events",
      ],
      dimensions: [
        "company_shopify_product_variant_breakage.product_title",
        "company_shopify_product_variant_breakage.product_id",
        "company_shopify_product_variant_breakage.broken_variant_count",
        "company_shopify_product_variant_breakage.total_variants",
        "company_shopify_product_variant_breakage.breakage_rate",
        "company_shopify_product_variant_breakage.inventory_health_status",
        "company_shopify_product_variant_breakage.has_unresolved_breaks",
        "company_shopify_product_variant_breakage.latest_sellout_date",
        "company_shopify_product_variant_breakage.latest_restock_date",
        "company_shopify_product_variant_breakage.total_sellout_events",
        "company_shopify_product_variant_breakage.total_restock_events",
        "company_shopify_product_variant_breakage.tracked_variant_count",
      ],
      timeDimensions: [
        {
          dimension: "company_shopify_product_variant_breakage.snapshot_date",
        },
      ],
    },
  },
  shopify_orders_economics_chart: {
    q: {
      measures: [
        "company_shopify_orders.revenue",
        "company_shopify_orders.net_revenue",
        "company_shopify_orders.gross_profit",
        "company_shopify_orders.net_pre_mktg_contribution",
        "company_shopify_orders.total_discounts",
      ],
      timeDimensions: [
        {
          dimension: "company_shopify_orders.order_date",
        },
      ],
      order: {
        "company_shopify_orders.order_date": "asc",
      },
    },
  },
  shopify_orders_by_channel_table: {
    q: {
      measures: [
        "company_shopify_orders.orders",
        "company_shopify_orders.customers",
        "company_shopify_orders.revenue",
        "company_shopify_orders.net_revenue",
        "company_shopify_orders.aov",
        "company_shopify_orders.total_discounts",
        "company_shopify_orders.discount_rate",
        "company_shopify_orders.gross_profit",
        "company_shopify_orders.gross_margin_percent",
        "company_shopify_orders.net_pre_mktg_contribution",
        "company_shopify_orders.ltv_90_day_revenue",
      ],
      dimensions: ["company_shopify_orders.channel"],
      timeDimensions: [
        {
          dimension: "company_shopify_orders.order_date",
        },
      ],
      order: {
        "company_shopify_orders.revenue": "desc",
      },
    },
  },
}
